import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Settings, User, Radio, Bell, Wallet, Save, Plus, Trash2, Check, Camera, Moon, Sun, Key, Copy, RefreshCw, QrCode, ArrowUpRight, ArrowDownLeft, AlertTriangle, Download, Upload, Eye, EyeOff, VolumeX, Volume2, BadgeCheck, Coins, Loader2, Film, ExternalLink, Users, ShieldAlert, RotateCcw, Search, Filter, FileText, Type, Minus, ALargeSmall, Info, BarChart3, ChevronRight, ChevronDown, Store, Mic2, CheckCircle, Lock, Unlock, ShieldCheck } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish, getRelays } from '../hooks/useNostrPublish';
import { useTipWallet, type TipWallet } from '../hooks/useTipWallet';
import { getBCHPrice } from '../hooks/useBCHPrice';
import {
  loadStoredNsec, isNsecEncrypted, enableNsecEncryption,
  disableNsecEncryption, changeNsecPin,
} from '../lib/nsecSigner';
import { validatePin } from '../lib/keyEncryption';
import { uploadImage } from '../lib/imageUpload';
import { nip19 } from 'nostr-tools';
import { BCHLogo } from '../components/Layout';
import { WizardConnectPanel } from '../components/WizardConnect';
import { invalidateProfileCache, useNostrProfile, useNostrQuery } from '../hooks/useNostrQuery';
import { invalidateBCHAddressCache, useBCHAddress, isValidBCHAddress, normalizeBCHAddress } from '../hooks/useBCHAddress';
import { useCommunityWallet, invalidateCommunityWalletCache } from '../hooks/useCommunityWallet';
import { useBusinessProfile } from '../hooks/useBusinessProfile';
import { useBusinessVerification } from '../hooks/useBusinessVerification';
import {
  BUSINESS_KIND, BUSINESS_CATEGORIES, BUSINESS_DAYS, buildBusinessEvent, retireBusinessEvent,
  PROFESSIONAL_TYPES, normalizeProfessionalType, type ProfessionalType,
} from '../lib/business';
import CreateBusinessModal from '../components/CreateBusinessModal';
import VerifyBusinessSection from '../components/VerifyBusinessSection';
import PromoteBusinessSection from '../components/PromoteBusinessSection';
import BusinessVerificationAdmin from '../components/BusinessVerificationAdmin';
import { exportBusinessNsec } from '../lib/identities';
import {
  invalidateNip05Cache, checkNameAvailability,
  refreshDirectory, getDirectoryNames, buildDirectoryJson,
} from '../lib/nip05verify';
import {
  auditClaims, describeVerdict, isSuspicious, type AuditResult,
} from '../lib/paymentAudit';
import { OPERATOR_PUBKEY, GRANT_ISSUERS, PLATFORM_FEE_ADDRESS, isOperatorPubkey, isDataSaverEnabled, setDataSaver } from '../lib/platform';
import { resolveTipTokens, getResolvedTipTokens, tokenBalanceUsd, baseUnitsToWhole, wholeToBaseUnits, type TipToken } from '../lib/tipTokens';
import { getBannedPubkeys, removeLocalBan, FLAG_KIND, UNBAN_TAG, onBanListChange } from '../lib/communityFlag';
import {
  getTurnConfig, setTurnConfig, hasTurn, buildStaticTurnUrls,
  cacheSharedTurnConfig, hasLocalTurnOverride, TURN_CONFIG_D_TAG,
  getCachedSharedTurnConfig as getCachedSharedTurn,
} from '../lib/rooms';

const VALID_TABS = ['profile', 'security', 'wallet', 'relays', 'media', 'muted', 'appearance', 'admin'] as const;
type SettingsTab = typeof VALID_TABS[number];

export default function SettingsPage() {
  const { user, login, logout, updateProfile, theme, toggleTheme } = useAppContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as SettingsTab | null;
  const initialTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'profile';
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);

  // Sync tab changes to URL
  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    if (tab === 'profile') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab }, { replace: true });
    }
  };

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-4">
        <Settings className="w-16 h-16 text-muted-foreground/30" />
        <h2 className="text-lg font-semibold">Settings</h2>
        <p className="text-sm text-muted-foreground text-center">
          Log in to manage your profile, relays, and wallet settings.
        </p>
        <button
          onClick={login}
          className="px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90"
        >
          Log in with Nostr
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <Settings className="w-5 h-5 text-primary" /> Settings
        </h1>
      </div>

      {/* Tabs — scrollable, larger touch targets on mobile */}
      <div className="flex border-b border-border overflow-x-auto scrollbar-none">
        {[
          { id: 'profile', icon: User, label: 'Profile' },
          { id: 'security', icon: ShieldCheck, label: 'Security' },
          { id: 'wallet', icon: Wallet, label: 'Wallet' },
          { id: 'relays', icon: Radio, label: 'Relays' },
          { id: 'media', icon: Film, label: 'Media' },
          { id: 'muted', icon: VolumeX, label: 'Muted' },
          { id: 'appearance', icon: Moon, label: 'Theme' },
          // Operator-only tools get their own tab so they aren't buried
          // at the bottom of an unrelated one.
          ...(isOperatorPubkey(user.pubkey)
            ? [{ id: 'admin', icon: ShieldAlert, label: 'Admin' }]
            : []),
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id as SettingsTab)}
            className={`flex items-center gap-1.5 px-4 py-3.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      <div className="p-4 max-w-2xl">
        {activeTab === 'profile' && <ProfileSettings user={user} updateProfile={updateProfile} />}
        {activeTab === 'security' && <SecuritySettings user={user} />}
        {activeTab === 'relays' && <RelaySettings />}
        {activeTab === 'wallet' && <WalletSettings />}
        {activeTab === 'media' && <MediaSettings />}
        {activeTab === 'muted' && (
          <>
            <MutedUsersSettings />
            <BannedUsersSettings />
          </>
        )}
        {activeTab === 'appearance' && <AppearanceSettings theme={theme} toggleTheme={toggleTheme} />}
        {activeTab === 'admin' && <AdminTab userPubkey={user.pubkey} />}
      </div>
    </div>
  );
}

function ProfileSettings({ user, updateProfile }: { user: any; updateProfile: (p: any) => void }) {
  const { publish } = useNostrPublish();
  const { profile: relayProfile } = useNostrProfile(user.pubkey);
  const { address: kind30078Address } = useBCHAddress(user.pubkey);
  const { address: existingCommunityWallet, split: existingSplit } = useCommunityWallet(user.pubkey);
  const [name, setName] = useState(user.name || '');
  const [about, setAbout] = useState(user.about || '');
  const [picture, setPicture] = useState(user.picture || '');
  const [banner, setBanner] = useState(user.banner || '');
  const [nip05, setNip05] = useState(user.nip05 || '');
  const [lud16, setLud16] = useState(user.lud16 || '');
  const [bchAddress, setBchAddress] = useState(user.bchAddress || '');
  const [communityWallet, setCommunityWallet] = useState('');
  const [communitySplit, setCommunitySplit] = useState(60); // % to community wallet
  const [communityWalletError, setCommunityWalletError] = useState('');
  // Professional profile (kind 30499) — a toggle that turns this account
  // into a discoverable BCH professional. The TYPE simply labels the kind
  // of professional. (Podcasts are unrelated — a fully standalone feature.)
  const { business: existingBusiness } = useBusinessProfile(user.pubkey);
  // Authoritative (issuer-signed) verification state for THIS account, so a
  // profile save re-stamps the verified-business marker instead of silently
  // dropping it. Only meaningful for business Pages, but harmless otherwise.
  const businessVerification = useBusinessVerification(user.pubkey);
  const [isBusiness, setIsBusiness] = useState(false);
  const [proType, setProType] = useState<ProfessionalType>('business');
  const [showCreateBiz, setShowCreateBiz] = useState(false);
  // A Business Page is its own identity. The old in-place toggle (turning
  // your PERSONAL account into a business) only still shows for accounts
  // that used it before, or when you're editing a Page's own listing.
  const actingAsBusiness = !!user.isBusiness;
  const showLegacyProCard = actingAsBusiness || !!existingBusiness || isBusiness;
  const [bizName, setBizName] = useState('');
  const [bizCategory, setBizCategory] = useState('other');
  const [bizLocation, setBizLocation] = useState('');
  const [bizOnlineOnly, setBizOnlineOnly] = useState(false);
  const [bizTagline, setBizTagline] = useState('');
  const [bizDescription, setBizDescription] = useState('');
  const [bizWebsite, setBizWebsite] = useState('');
  const [bizPhone, setBizPhone] = useState('');
  const [bizEmail, setBizEmail] = useState('');
  const [bizHours, setBizHours] = useState<Record<string, string>>({});
  const [bizAcceptsCashtokens, setBizAcceptsCashtokens] = useState(false);
  const [bizGallery, setBizGallery] = useState<string[]>([]);
  const [bizShowMore, setBizShowMore] = useState(false);
  const bizHydratedRef = useRef(false);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Track whether the user has manually edited each field — once touched, never overwrite
  const touchedRef = useRef<Set<string>>(new Set());
  const bchHydratedRef = useRef(false);
  const cwHydratedRef = useRef(false);

  // When the dedicated kind 30078 BCH address event loads, fill the field.
  // This is the authoritative source — more reliable than user.bchAddress.
  useEffect(() => {
    if (bchHydratedRef.current || !kind30078Address) return;
    bchHydratedRef.current = true;
    if (!touchedRef.current.has('bchAddress')) {
      setBchAddress(prev => prev || kind30078Address.replace(/^bitcoincash:/i, ''));
    }
  }, [kind30078Address]);

  // Hydrate business profile from relay (once).
  useEffect(() => {
    if (bizHydratedRef.current || !existingBusiness) return;
    bizHydratedRef.current = true;
    setIsBusiness(true);
    if (!touchedRef.current.has('proType')) setProType(normalizeProfessionalType(existingBusiness.proType));
    if (!touchedRef.current.has('bizName')) setBizName(existingBusiness.name || '');
    if (!touchedRef.current.has('bizCategory')) setBizCategory(existingBusiness.category || 'other');
    if (!touchedRef.current.has('bizLocation')) setBizLocation(existingBusiness.location || '');
    if (!touchedRef.current.has('bizOnlineOnly')) setBizOnlineOnly(!!existingBusiness.onlineOnly);
    if (!touchedRef.current.has('bizTagline')) setBizTagline(existingBusiness.tagline || '');
    if (!touchedRef.current.has('bizDescription')) setBizDescription(existingBusiness.description || '');
    if (!touchedRef.current.has('bizWebsite')) setBizWebsite(existingBusiness.website || '');
    if (!touchedRef.current.has('bizPhone')) setBizPhone(existingBusiness.phone || '');
    if (!touchedRef.current.has('bizEmail')) setBizEmail(existingBusiness.email || '');
    if (!touchedRef.current.has('bizHours')) setBizHours(existingBusiness.hours || {});
    if (!touchedRef.current.has('bizAcceptsCashtokens')) setBizAcceptsCashtokens(!!existingBusiness.acceptsCashtokens);
    if (!touchedRef.current.has('bizGallery')) setBizGallery(existingBusiness.gallery || []);
    // If they've filled any extended field, open the "more details" panel.
    if (existingBusiness.tagline || existingBusiness.description || existingBusiness.website ||
        existingBusiness.phone || existingBusiness.email ||
        (existingBusiness.hours && Object.keys(existingBusiness.hours).length > 0) ||
        (existingBusiness.gallery && existingBusiness.gallery.length > 0)) {
      setBizShowMore(true);
    }
  }, [existingBusiness]);



  // Hydrate community wallet address + split from relay
  useEffect(() => {
    if (cwHydratedRef.current || !existingCommunityWallet) return;
    cwHydratedRef.current = true;
    if (!touchedRef.current.has('communityWallet')) {
      setCommunityWallet(prev => prev || existingCommunityWallet.replace(/^bitcoincash:/i, ''));
    }
    if (!touchedRef.current.has('communitySplit')) {
      setCommunitySplit(existingSplit);
    }
  }, [existingCommunityWallet, existingSplit]);

  // When relay profile arrives (or updates via stale-while-revalidate), fill
  // any form fields that the user hasn't manually edited yet.
  // No one-shot ref — this runs each time relayProfile changes, so both the
  // initial stale cache hit AND the fresh relay response can populate fields.
  useEffect(() => {
    if (!relayProfile) return;
    if (!touchedRef.current.has('name') && !name && (relayProfile.display_name || relayProfile.name))
      setName(relayProfile.display_name || relayProfile.name);
    if (!touchedRef.current.has('about') && !about && relayProfile.about)
      setAbout(relayProfile.about);
    if (!touchedRef.current.has('picture') && !picture && relayProfile.picture)
      setPicture(relayProfile.picture);
    if (!touchedRef.current.has('banner') && !banner && relayProfile.banner)
      setBanner(relayProfile.banner);
    if (!touchedRef.current.has('nip05') && !nip05 && relayProfile.nip05)
      setNip05(relayProfile.nip05);
    if (!touchedRef.current.has('lud16') && !lud16 && relayProfile.lud16)
      setLud16(relayProfile.lud16);
    if (!touchedRef.current.has('bchAddress') && !bchAddress && relayProfile.bch_address)
      setBchAddress(relayProfile.bch_address.replace(/^bitcoincash:/i, ''));
  }, [relayProfile, name, about, picture, banner, nip05, lud16, bchAddress]);

  // Also sync if user object itself gets hydrated (e.g. from background relay fetch)
  useEffect(() => {
    if (!touchedRef.current.has('name') && !name && user.name) setName(user.name);
    if (!touchedRef.current.has('about') && !about && user.about) setAbout(user.about);
    if (!touchedRef.current.has('picture') && !picture && user.picture) setPicture(user.picture);
    if (!touchedRef.current.has('banner') && !banner && user.banner) setBanner(user.banner);
    if (!touchedRef.current.has('nip05') && !nip05 && user.nip05) setNip05(user.nip05);
    if (!touchedRef.current.has('lud16') && !lud16 && user.lud16) setLud16(user.lud16);
    if (!touchedRef.current.has('bchAddress') && !bchAddress && user.bchAddress) setBchAddress(user.bchAddress.replace(/^bitcoincash:/i, ''));
  }, [user.name, user.about, user.picture, user.banner, user.nip05, user.lud16, user.bchAddress]);

  const [bchError, setBchError] = useState('');
  const [nip05Error, setNip05Error] = useState('');
  const [saveError, setSaveError] = useState('');

  const handleSave = async () => {
    setBchError('');
    setCommunityWalletError('');
    setNip05Error('');
    setSaveError('');

    // Block users from manually typing a NEW @bchnostr.com name — those must
    // be claimed through the paid verification flow. This guard ONLY applies
    // when the user is *introducing* a @bchnostr.com value they didn't already
    // have. A name that already matches their existing profile / user record
    // was legitimately granted (paid NIP-05 or an operator-verified business),
    // so we must never re-litigate it here — otherwise an unrelated edit (e.g.
    // setting the BCH address) silently fails when the well-known directory
    // hasn't propagated the brand-new name yet, or on any transient fetch
    // error. That was the "brand new verified account can't set its BCH
    // wallet" bug: the guard aborted the whole save before the address was
    // ever published.
    const trimmedNip05 = nip05.trim().toLowerCase();
    // "Already theirs" = matches the value we loaded from the user record OR
    // the relay profile. Either source proves the name was assigned to them
    // (paid claim / operator-granted business), so it isn't a fresh manual
    // attempt and must not be re-validated on save.
    const existingNip05Values = [
      (user.nip05 || '').trim().toLowerCase(),
      (relayProfile?.nip05 || '').trim().toLowerCase(),
    ].filter(Boolean);
    const isNewBchnostrClaim =
      trimmedNip05.endsWith('@bchnostr.com') &&
      !existingNip05Values.includes(trimmedNip05) &&
      // Only treat it as user-typed when they actually touched the field.
      touchedRef.current.has('nip05');
    if (isNewBchnostrClaim) {
      // Check if this NIP-05 actually resolves to their pubkey
      try {
        const claimedName = trimmedNip05.replace('@bchnostr.com', '');
        const resp = await fetch(`https://bchnostr.com/.well-known/nostr.json?name=${encodeURIComponent(claimedName)}`);
        const data = await resp.json();
        const resolvedPubkey = data?.names?.[claimedName];
        if (resolvedPubkey !== user.pubkey) {
          setNip05Error('You must claim your @bchnostr.com name using the verification section below ($1 in BCH). You cannot set it manually.');
          return;
        }
      } catch {
        setNip05Error('Could not verify your @bchnostr.com claim. Please try again or use the verification section below.');
        return;
      }
    }

    // Validate BCH address if provided
    const rawAddr = bchAddress.trim();
    let normalizedAddr = '';
    if (rawAddr) {
      const normalized = normalizeBCHAddress(rawAddr);
      if (!normalized) {
        setBchError('Invalid BCH address. Please enter a CashAddr format address (starts with q or p).');
        return;
      }
      normalizedAddr = normalized;
    }

    // Validate community wallet address if provided
    const rawCW = communityWallet.trim();
    let normalizedCW = '';
    if (rawCW) {
      const normalized = normalizeBCHAddress(rawCW);
      if (!normalized) {
        setCommunityWalletError('Invalid BCH address. Please enter a CashAddr format address (starts with q or p).');
        return;
      }
      // Warn if it's the same as the personal address
      if (normalizedAddr && normalized === normalizedAddr) {
        setCommunityWalletError('Community wallet should be a different address from your personal BCH address.');
        return;
      }
      normalizedCW = normalized;
    }

    setSaving(true);
    try {
      // Store the bare payload in the kind 0 profile (without bitcoincash: prefix)
      const bareAddr = normalizedAddr ? normalizedAddr.replace('bitcoincash:', '') : '';

      // MERGE onto the existing kind 0 rather than replacing it with this
      // form's narrow field set. A kind 0 is replaceable, so publishing only
      // {name,about,picture,banner,nip05,lud16,bch_address} WIPED every other
      // field the user had — most damagingly `display_name` (which many
      // clients use as the primary name) and any `website`/`lud06`/custom
      // fields. That's why a profile ended up with name:"" and a vanished
      // display name after saving a BCH address here. We now start from the
      // freshest known kind 0, overlay only the fields this form owns, and
      // never write an EMPTY value over a non-empty existing one.
      const existingProfile: Record<string, unknown> = {
        ...(relayProfile && typeof relayProfile === 'object' ? relayProfile : {}),
      };
      const keepIfBlank = (key: string, next: string) => {
        const trimmed = next.trim();
        if (trimmed) return trimmed;
        // Blank field: keep whatever was already there (don't clobber).
        const prev = existingProfile[key];
        return typeof prev === 'string' ? prev : '';
      };
      const profile: Record<string, unknown> = {
        ...existingProfile,
        name: keepIfBlank('name', name),
        about,
        picture,
        banner,
        nip05: keepIfBlank('nip05', nip05),
        lud16,
        bch_address: bareAddr,
      };
      // If the user has a display_name and cleared/never set `name`, mirror it
      // so BCHNostr (which reads display_name || name) and name-only clients
      // both resolve to something.
      if (!profile.name && typeof existingProfile.display_name === 'string' && existingProfile.display_name.trim()) {
        profile.name = existingProfile.display_name;
      }
      await publish({
        kind: 0,
        content: JSON.stringify(profile),
        tags: [],
        created_at: Math.floor(Date.now() / 1000),
      });

      // ALSO publish BCH address as kind 30078 event — this is what the tip system reads
      if (normalizedAddr) {
        await publish({
          kind: 30078,
          content: normalizedAddr,
          tags: [
            ['d', 'bchnostr/bch-address'],
            ['alt', 'BCH (Bitcoin Cash) receiving address for tips'],
          ],
          created_at: Math.floor(Date.now() / 1000),
        });
      }

      // Publish community wallet as kind 30078 with JSON content (address + split)
      if (normalizedCW) {
        const cwContent = JSON.stringify({ address: normalizedCW, split: communitySplit });
        await publish({
          kind: 30078,
          content: cwContent,
          tags: [
            ['d', 'bchnostr/community-wallet'],
            ['alt', `BCH community tip wallet — ${communitySplit}% circulating`],
          ],
          created_at: Math.floor(Date.now() / 1000),
        });
      } else if (existingCommunityWallet && !rawCW) {
        // User cleared the field — publish empty to remove
        await publish({
          kind: 30078,
          content: '',
          tags: [
            ['d', 'bchnostr/community-wallet'],
            ['alt', 'Community wallet removed'],
          ],
          created_at: Math.floor(Date.now() / 1000),
        });
      }

      // Publish / retire the business profile (kind 30499) based on the toggle.
      if (isBusiness) {
        // Drop empty day entries so we don't persist blank hours.
        const cleanHours = Object.fromEntries(
          Object.entries(bizHours).filter(([, v]) => v.trim()),
        );
        // Preserve the verified-business marker. A kind 30499 event is
        // replaceable, so re-publishing it WITHOUT the marker silently strips
        // a verification the operator/backend granted. Re-stamp from the
        // authoritative issuer-signed grant when the account is currently
        // verified, falling back to the existing self-asserted marker. Take
        // the LATER of the two so a save never shortens the badge's validity.
        const grantedUntil = businessVerification.status === 'VERIFIED'
          ? businessVerification.expiresAt
          : 0;
        const markerUntil = existingBusiness?.verifiedUntil || 0;
        const verifiedUntil = Math.max(grantedUntil, markerUntil) || undefined;
        const { tags, content } = buildBusinessEvent({
          name: bizName.trim() || name,
          category: bizCategory,
          proType,
          location: bizOnlineOnly ? '' : bizLocation,
          bch: normalizedAddr || undefined,
          // Preserve geohash from the existing listing so editing the profile
          // never drops the business off the map / near-me discovery.
          geohash: existingBusiness?.geohash || undefined,
          onlineOnly: bizOnlineOnly,
          verifiedUntil,
          content: {
            tagline: bizTagline.trim() || undefined,
            description: bizDescription.trim() || undefined,
            website: bizWebsite.trim() || undefined,
            phone: bizPhone.trim() || undefined,
            email: bizEmail.trim() || undefined,
            hours: Object.keys(cleanHours).length > 0 ? cleanHours : undefined,
            acceptsCashtokens: bizAcceptsCashtokens || undefined,
            gallery: bizGallery.length > 0 ? bizGallery : undefined,
            // Carry over socials — they aren't edited on this form, so omitting
            // them here would wipe any the business set elsewhere.
            socials: existingBusiness?.socials,
          },
        });
        await publish({
          kind: BUSINESS_KIND,
          content,
          tags,
          created_at: Math.floor(Date.now() / 1000),
        });
      } else if (existingBusiness) {
        // Toggle turned off — retire the existing profile.
        const { tags, content } = retireBusinessEvent();
        await publish({
          kind: BUSINESS_KIND,
          content,
          tags,
          created_at: Math.floor(Date.now() / 1000),
        });
      }

      // Podcasts are a fully standalone feature (kind 30078, managed on the
      // BCH Podcasts page). Settings neither publishes nor retires a show —
      // the professional-profile toggle has nothing to do with podcasts.

      updateProfile({ name, about, picture, banner, nip05, lud16, bchAddress: bareAddr });
      // Clear caches so new data shows up immediately
      invalidateProfileCache(user.pubkey);
      invalidateBCHAddressCache(user.pubkey);
      invalidateCommunityWalletCache(user.pubkey);
      // Update the display field to show the clean version
      if (bareAddr) setBchAddress(bareAddr);
      if (normalizedCW) setCommunityWallet(normalizedCW.replace('bitcoincash:', ''));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('Failed to save profile:', e);
      const msg = e instanceof Error ? e.message : 'Unknown error';
      if (msg.includes('No Nostr extension') || msg.includes('nostr')) {
        setSaveError('Signing failed — your Nostr signer may not be ready. Try refreshing the page and saving again.');
      } else {
        setSaveError('Failed to save profile: ' + msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Business Pages promo — the new model: a business is its own identity,
          not a toggle on your personal account. Shown to personal accounts
          that don't already have a legacy in-place professional profile. */}
      {!showLegacyProCard && (
        <div className="rounded-2xl border-2 border-primary/40 bg-gradient-to-br from-primary/10 to-emerald-500/5 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-primary/10 shrink-0">
              <Store className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold">Run a business?</h3>
              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                Create a <strong>Business Page</strong> — its own profile, posts, and BCH tip
                wallet, kept separate from your personal account. You own it and switch into it
                any time.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowCreateBiz(true)}
            className="mt-3 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
          >
            <Store className="w-3.5 h-3.5" /> Create a Business Page
          </button>
        </div>
      )}
      {showCreateBiz && <CreateBusinessModal onClose={() => setShowCreateBiz(false)} />}

      {/* Professional Profile — legacy in-place listing (also used when
          editing a Business Page's own listing). */}
      {showLegacyProCard && (
      <div className={`rounded-2xl border-2 overflow-hidden transition-colors ${
        isBusiness ? 'border-primary bg-primary/5' : 'border-primary/40 bg-gradient-to-br from-primary/10 to-emerald-500/5'
      }`}>
        <div className="flex items-center gap-3 p-4">
          <div className={`p-2.5 rounded-xl shrink-0 ${isBusiness ? 'bg-primary/15' : 'bg-primary/10'}`}>
            <Store className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold">Professional Profile</h3>
              <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/15 text-primary">
                New
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isBusiness
                ? `You're listed as a BCH ${PROFESSIONAL_TYPES.find(t => t.id === proType)?.label.toLowerCase() || 'business'}.`
                : 'Accept Bitcoin Cash? List your business or project so people can find you.'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isBusiness}
            onClick={() => { touchedRef.current.add('isBusiness'); setIsBusiness(v => !v); }}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${
              isBusiness ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                isBusiness ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {isBusiness && (
          <div className="space-y-3 px-4 pb-4 border-t border-primary/15 pt-3">
            {/* ── Professional type ── */}
            {/* The type simply labels the professional for discovery. */}
            <div>
              <label className="text-[11px] font-medium text-muted-foreground">Professional Type</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mt-1.5">
                {PROFESSIONAL_TYPES.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => { touchedRef.current.add('proType'); setProType(t.id); }}
                    title={t.hint}
                    className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                      proType === t.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:text-foreground hover:border-primary/40'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                {PROFESSIONAL_TYPES.find(t => t.id === proType)?.hint}
              </p>
            </div>

            <div>
              <label className="text-[11px] font-medium text-muted-foreground">
                Business Name
              </label>
              <input
                value={bizName}
                onChange={e => { touchedRef.current.add('bizName'); setBizName(e.target.value); }}
                placeholder={name || 'Your business name'}
                maxLength={100}
                className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">Leave blank to use your display name.</p>
            </div>

            {/* Storefront fields. */}
            <>
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground">Tagline</label>
                  <input
                    value={bizTagline}
                    onChange={e => { touchedRef.current.add('bizTagline'); setBizTagline(e.target.value); }}
                    placeholder="Best flat white in town"
                    maxLength={120}
                    className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-medium text-muted-foreground">Category</label>
                  <select
                    value={bizCategory}
                    onChange={e => { touchedRef.current.add('bizCategory'); setBizCategory(e.target.value); }}
                    className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    {BUSINESS_CATEGORIES.map(c => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <label className="flex items-center gap-2 text-[11px] font-medium text-foreground">
                  <input
                    type="checkbox"
                    checked={bizOnlineOnly}
                    onChange={e => { touchedRef.current.add('bizOnlineOnly'); setBizOnlineOnly(e.target.checked); }}
                    className="accent-primary"
                  />
                  Online only (no physical location)
                </label>

                {!bizOnlineOnly && (
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground">Location</label>
                    <input
                      value={bizLocation}
                      onChange={e => { touchedRef.current.add('bizLocation'); setBizLocation(e.target.value); }}
                      placeholder="123 Main St, Springfield"
                      maxLength={140}
                      className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                )}

                {/* More details expander */}
                <button
                  type="button"
                  onClick={() => setBizShowMore(v => !v)}
                  className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                >
                  {bizShowMore ? <ChevronRight className="w-3 h-3 rotate-90 transition-transform" /> : <ChevronRight className="w-3 h-3 transition-transform" />}
                  {bizShowMore ? 'Fewer details' : 'More details (description, hours, contact)'}
                </button>
            </>

            {bizShowMore && (
              <div className="space-y-3 rounded-lg border border-border bg-background/50 p-3">
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground">Description</label>
                  <textarea
                    value={bizDescription}
                    onChange={e => { touchedRef.current.add('bizDescription'); setBizDescription(e.target.value); }}
                    placeholder="Tell customers about your business..."
                    rows={3}
                    maxLength={600}
                    className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground">Website</label>
                    <input
                      value={bizWebsite}
                      onChange={e => { touchedRef.current.add('bizWebsite'); setBizWebsite(e.target.value); }}
                      placeholder="example.com"
                      maxLength={200}
                      className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground">Phone</label>
                    <input
                      value={bizPhone}
                      onChange={e => { touchedRef.current.add('bizPhone'); setBizPhone(e.target.value); }}
                      placeholder="+1 555 123 4567"
                      maxLength={40}
                      className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-medium text-muted-foreground">Email</label>
                  <input
                    value={bizEmail}
                    onChange={e => { touchedRef.current.add('bizEmail'); setBizEmail(e.target.value); }}
                    placeholder="hi@example.com"
                    maxLength={120}
                    className="w-full bg-background rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-medium text-muted-foreground">Hours</label>
                  <div className="space-y-1.5 mt-1">
                    {BUSINESS_DAYS.map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className="w-9 text-[11px] text-muted-foreground shrink-0">{label}</span>
                        <input
                          value={bizHours[key] || ''}
                          onChange={e => {
                            touchedRef.current.add('bizHours');
                            setBizHours(prev => ({ ...prev, [key]: e.target.value }));
                          }}
                          placeholder="09:00–17:00 or Closed"
                          maxLength={40}
                          className="flex-1 bg-background rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2 text-[11px] font-medium text-foreground">
                  <input
                    type="checkbox"
                    checked={bizAcceptsCashtokens}
                    onChange={e => { touchedRef.current.add('bizAcceptsCashtokens'); setBizAcceptsCashtokens(e.target.checked); }}
                    className="accent-primary"
                  />
                  Accepts CashTokens
                </label>

                <BusinessGalleryField
                  images={bizGallery}
                  onChange={imgs => { touchedRef.current.add('bizGallery'); setBizGallery(imgs); }}
                />
              </div>
            )}

            <div className="flex items-start gap-2 text-[10px] text-muted-foreground">
              <Info className="w-3 h-3 shrink-0 mt-0.5" />
              <p>
                Your <strong className="text-foreground">BCH Address</strong> below is used as the
                business's receiving address. Businesses appear in the BCH Merchants directory.
              </p>
            </div>
          </div>
        )}
      </div>
      )}

      {actingAsBusiness && <BusinessKeyBackup pubkey={user.pubkey} />}

      {/* Paid business verification — shown once the account has a business
          profile (acting as a Business Page, or a personal key carrying a
          30499). Reuses the existing business identity; never creates one. */}
      {(actingAsBusiness || existingBusiness) && (
        <VerifyBusinessSection pubkey={user.pubkey} />
      )}

      {/* Sponsored feed ads — verified businesses can pay BCH to appear as a
          Sponsored card in the main feed. The section self-gates on
          verification; shown alongside the verification card. */}
      {(actingAsBusiness || existingBusiness) && (
        <PromoteBusinessSection pubkey={user.pubkey} />
      )}

      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground">Display Name</label>
          <input value={name} onChange={e => { touchedRef.current.add('name'); setName(e.target.value); }} className="w-full bg-muted rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary" />
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">About</label>
          <textarea value={about} onChange={e => { touchedRef.current.add('about'); setAbout(e.target.value); }} className="w-full bg-muted rounded-lg px-3 py-2 text-sm mt-1 resize-none focus:outline-none focus:ring-1 focus:ring-primary" rows={3} />
        </div>
        <ImageUploadField label="Profile Picture" value={picture} onChange={(url) => { touchedRef.current.add('picture'); setPicture(url); }} preview="avatar" />
        <ImageUploadField label="Banner" value={banner} onChange={(url) => { touchedRef.current.add('banner'); setBanner(url); }} preview="banner" />
        {nip05Error && (
          <p className="text-xs text-red-500 -mt-1">{nip05Error}</p>
        )}
        {/* NIP-05 Claim Section */}
        <Nip05ClaimSection
          currentNip05={nip05}
          pubkey={user.pubkey}
          onClaimed={(newNip05) => {
            touchedRef.current.add('nip05');
            setNip05(newNip05);
            // Auto-save profile with the new nip05 immediately. Merge onto the
            // existing kind 0 so this narrow write never wipes display_name or
            // other fields (see handleSave for the full rationale).
            const base: Record<string, unknown> = {
              ...(relayProfile && typeof relayProfile === 'object' ? relayProfile : {}),
            };
            const mergedName = (name.trim() || (typeof base.name === 'string' ? base.name : '')
              || (typeof base.display_name === 'string' ? base.display_name : ''));
            const profile: Record<string, unknown> = {
              ...base,
              name: mergedName,
              about,
              picture,
              banner,
              nip05: newNip05,
              lud16,
              bch_address: bchAddress,
            };
            publish({
              kind: 0,
              content: JSON.stringify(profile),
              tags: [],
              created_at: Math.floor(Date.now() / 1000),
            }).then(() => {
              updateProfile({ name, about, picture, banner, nip05: newNip05, lud16, bchAddress });
              invalidateProfileCache(user.pubkey);
            }).catch(() => {});
          }}
        />
        <div>
          <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <BCHLogo size={14} /> BCH Address
          </label>
          <input
            value={bchAddress}
            onChange={e => { touchedRef.current.add('bchAddress'); setBchAddress(e.target.value); setBchError(''); }}
            placeholder={kind30078Address ? 'Loading…' : 'q... (CashAddr format)'}
            className={`w-full bg-muted rounded-lg px-3 py-2 text-sm mt-1 font-mono focus:outline-none focus:ring-1 ${bchError ? 'focus:ring-red-500 ring-1 ring-red-500/50' : 'focus:ring-primary'}`}
          />
          {bchError ? (
            <p className="text-[10px] text-red-500 mt-0.5">{bchError}</p>
          ) : (
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Your public BCH address for receiving tips — saved to Nostr so it works on any device
            </p>
          )}
        </div>

        {/* Community Tip Wallet */}
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 space-y-3">
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-md bg-emerald-500/10">
              <RefreshCw className="w-3.5 h-3.5 text-emerald-500" />
            </div>
            <div>
              <label className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">Community Tip Wallet</label>
              <p className="text-[10px] text-muted-foreground">Keep BCH circulating in the community</p>
            </div>
          </div>
          <input
            value={communityWallet}
            onChange={e => { touchedRef.current.add('communityWallet'); setCommunityWallet(e.target.value); setCommunityWalletError(''); }}
            placeholder={existingCommunityWallet ? 'Loading…' : 'q... (separate address for community tips)'}
            className={`w-full bg-muted rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 ${communityWalletError ? 'focus:ring-red-500 ring-1 ring-red-500/50' : 'focus:ring-emerald-500'}`}
          />
          {communityWalletError && (
            <p className="text-[10px] text-red-500">{communityWalletError}</p>
          )}

          {/* Tip Split Slider — only show when community wallet address is set */}
          {communityWallet.trim() && !communityWalletError && (
            <div className="space-y-2 pt-1">
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-medium text-foreground">Tip Split</label>
                <span className="text-[10px] text-muted-foreground">
                  When someone tips you, how should it be divided?
                </span>
              </div>

              {/* Split visualization bar */}
              <div className="relative h-7 rounded-lg overflow-hidden bg-muted border border-border">
                <div
                  className="absolute inset-y-0 left-0 bg-emerald-500/20 border-r border-emerald-500/30 transition-all duration-150 flex items-center justify-center"
                  style={{ width: `${communitySplit}%` }}
                >
                  {communitySplit >= 25 && (
                    <span className="text-[9px] font-semibold text-emerald-600 dark:text-emerald-400 flex items-center gap-0.5">
                      <RefreshCw className="w-2.5 h-2.5" /> {communitySplit}%
                    </span>
                  )}
                </div>
                <div
                  className="absolute inset-y-0 right-0 bg-primary/10 flex items-center justify-center transition-all duration-150"
                  style={{ width: `${100 - communitySplit}%` }}
                >
                  {(100 - communitySplit) >= 25 && (
                    <span className="text-[9px] font-semibold text-primary flex items-center gap-0.5">
                      <Wallet className="w-2.5 h-2.5" /> {100 - communitySplit}%
                    </span>
                  )}
                </div>
              </div>

              {/* Slider */}
              <input
                type="range"
                min={0}
                max={100}
                step={10}
                value={communitySplit}
                onChange={e => { touchedRef.current.add('communitySplit'); setCommunitySplit(Number(e.target.value)); }}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer accent-emerald-500 bg-muted"
              />

              {/* Preset buttons */}
              <div className="flex gap-1.5">
                {[
                  { label: '100% Circulate', value: 100 },
                  { label: '80/20', value: 80 },
                  { label: '60/40', value: 60 },
                  { label: '40/60', value: 40 },
                  { label: '100% Personal', value: 0 },
                ].map(preset => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => { touchedRef.current.add('communitySplit'); setCommunitySplit(preset.value); }}
                    className={`flex-1 py-1 rounded-md text-[9px] font-medium transition-colors ${
                      communitySplit === preset.value
                        ? 'bg-emerald-500 text-white'
                        : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Split explanation */}
              <div className="flex items-start gap-2 text-[10px] text-muted-foreground">
                <Info className="w-3 h-3 shrink-0 mt-0.5" />
                <p>
                  {communitySplit === 100
                    ? 'All tips go to your community wallet — maximum circulation!'
                    : communitySplit === 0
                      ? 'All tips go to your personal wallet. Community wallet is displayed but won\'t receive tips.'
                      : `${communitySplit}% of each tip goes to your community wallet (circulating), ${100 - communitySplit}% to your personal wallet. Split happens in a single transaction — no extra fees.`
                  }
                </p>
              </div>
            </div>
          )}

          {!communityWalletError && !communityWallet.trim() && (
            <div className="text-[10px] text-muted-foreground space-y-1">
              <p>
                <strong className="text-foreground">How it works:</strong> Set a dedicated BCH address just for tipping within BCHNostr.
                Tips you receive go here, and you re-tip from the same wallet — keeping BCH flowing through the community.
              </p>
              <p>
                Use a separate wallet (Electron Cash, Paytaca, etc.) from your personal funds.
                Your profile will show a <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 text-[9px] font-medium"><RefreshCw className="w-2 h-2" /> Circulating</span> badge
                and your circulation score — showing you're helping build the BCH economy.
              </p>
            </div>
          )}
        </div>

      </div>
      {saveError && (
        <div className="flex items-start gap-2 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2.5">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
          <p className="text-xs text-red-500">{saveError}</p>
        </div>
      )}
      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
      >
        {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
        {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Profile'}
      </button>

      {/* Your Keys */}
      <YourKeys pubkey={user.pubkey} loginMethod={user.loginMethod} />

      {/* Key & wallet PIN encryption now lives in the dedicated Security tab. */}
      <Link
        to="/settings?tab=security"
        className="mt-4 flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 hover:bg-muted/50 transition-colors"
      >
        <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
        <span className="text-sm font-medium flex-1">Protect your keys with a PIN</span>
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </Link>

      {/* Follow List Recovery */}
      <FollowListRecovery pubkey={user.pubkey} />
    </div>
  );
}

// ── Follow List Recovery Tool ─────────────────────────────────────
// Scans multiple relays for older kind 3 (contact list) events.
// If it finds a list with more follows than the current one, offers
// a one-tap restore. This helps users who lost follows due to bugs
// or misbehaving clients.

const RECOVERY_RELAYS = [
  'wss://nos.lol',
  'wss://relay.primal.net',
  'wss://relay.nostr.net',
  'wss://relay.snort.social',
  'wss://purplepag.es',
  'wss://relay.nostr.bg',
  'wss://nostr.wine',
  'wss://nostr-pub.wellorder.net',
];

interface FoundContactList {
  follows: string[];
  createdAt: number;
  relayUrl: string;
  eventId: string;
}

function FollowListRecovery({ pubkey }: { pubkey: string }) {
  const { publish } = useNostrPublish();
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [scanProgress, setScanProgress] = useState('');
  const [currentCount, setCurrentCount] = useState<number | null>(null);
  const [foundLists, setFoundLists] = useState<FoundContactList[]>([]);
  const [restoring, setRestoring] = useState(false);
  const [restored, setRestored] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bestFound = foundLists.length > 0
    ? foundLists.reduce((best, item) => item.follows.length > best.follows.length ? item : best)
    : null;

  const hasBetterList = bestFound && currentCount !== null && bestFound.follows.length > currentCount;

  const handleScan = async () => {
    setScanning(true);
    setScanned(false);
    setError(null);
    setFoundLists([]);
    setRestored(false);
    setScanProgress('Connecting to relays...');

    try {
      const results: FoundContactList[] = [];
      let currentBest: { follows: string[]; ts: number } = { follows: [], ts: 0 };
      let relaysDone = 0;

      // Query each relay independently so we can find lists that some relays
      // have purged but others still retain
      const promises = RECOVERY_RELAYS.map(relayUrl =>
        new Promise<void>((resolve) => {
          const timeout = setTimeout(() => {
            relaysDone++;
            setScanProgress(`Scanned ${relaysDone}/${RECOVERY_RELAYS.length} relays...`);
            resolve();
          }, 10000);

          try {
            const ws = new WebSocket(relayUrl);
            const subId = 'rec_' + Math.random().toString(36).slice(2, 8);
            const events: any[] = [];

            ws.onopen = () => {
              // Request up to 20 kind 3 events — relays that store event history
              // may return older versions that have more follows
              ws.send(JSON.stringify(['REQ', subId, {
                kinds: [3],
                authors: [pubkey],
                limit: 20,
              }]));
            };

            ws.onmessage = (msg) => {
              try {
                const data = JSON.parse(msg.data);
                if (data[0] === 'EVENT' && data[1] === subId) {
                  events.push(data[2]);
                }
                if (data[0] === 'EOSE' && data[1] === subId) {
                  clearTimeout(timeout);

                  // Process all events from this relay
                  for (const ev of events) {
                    const follows = (ev.tags || [])
                      .filter((t: any) => t[0] === 'p' && t[1]?.length === 64)
                      .map((t: any) => t[1]);

                    if (follows.length > 0) {
                      results.push({
                        follows,
                        createdAt: ev.created_at,
                        relayUrl,
                        eventId: ev.id,
                      });

                      // Track the "current" (newest) list
                      if (ev.created_at > currentBest.ts) {
                        currentBest = { follows, ts: ev.created_at };
                      }
                    }
                  }

                  relaysDone++;
                  setScanProgress(`Scanned ${relaysDone}/${RECOVERY_RELAYS.length} relays...`);
                  try { ws.close(); } catch {}
                  resolve();
                }
              } catch {}
            };

            ws.onerror = () => {
              clearTimeout(timeout);
              relaysDone++;
              setScanProgress(`Scanned ${relaysDone}/${RECOVERY_RELAYS.length} relays...`);
              resolve();
            };

            ws.onclose = () => {
              clearTimeout(timeout);
              resolve();
            };
          } catch {
            clearTimeout(timeout);
            relaysDone++;
            resolve();
          }
        })
      );

      await Promise.all(promises);

      // Deduplicate by event ID, sort by follow count descending
      const seen = new Set<string>();
      const uniqueResults = results.filter(r => {
        const key = r.eventId;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).sort((a, b) => b.follows.length - a.follows.length);

      setFoundLists(uniqueResults);
      setCurrentCount(currentBest.follows.length);
      setScanned(true);
      setScanProgress('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleRestore = async (list: FoundContactList) => {
    setRestoring(true);
    setError(null);
    try {
      await publish({
        kind: 3,
        content: '',
        tags: list.follows.map(f => ['p', f]),
        created_at: Math.floor(Date.now() / 1000),
      });

      // Update the localStorage backup too
      try {
        localStorage.setItem('bchnostr:follows:' + pubkey, JSON.stringify(list.follows));
        localStorage.setItem('bchnostr:follows-ts:' + pubkey, String(Math.floor(Date.now() / 1000)));
      } catch {}

      setRestored(true);
      setCurrentCount(list.follows.length);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const formatDate = (ts: number) => {
    const d = new Date(ts * 1000);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-3 pt-4 border-t border-border">
      <div className="flex items-center gap-2">
        <Users className="w-4 h-4 text-muted-foreground" />
        <h3 className="text-sm font-bold">Follow List Recovery</h3>
      </div>
      <p className="text-xs text-muted-foreground">
        Lost your following list? This tool scans multiple relays for older versions of your contact list and lets you restore the one with the most follows.
      </p>

      {!scanned && !scanning && (
        <button
          onClick={handleScan}
          className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Scan Relays for Lost Follows
        </button>
      )}

      {scanning && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-muted/50 border border-border">
          <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />
          <p className="text-sm text-muted-foreground">{scanProgress || 'Scanning...'}</p>
        </div>
      )}

      {scanned && !restored && (
        <div className="space-y-3">
          {/* Current state */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border">
            <User className="w-4 h-4 text-muted-foreground shrink-0" />
            <p className="text-sm">
              Your current contact list has <strong>{currentCount}</strong> follow{currentCount !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Best found list */}
          {hasBetterList && bestFound && (
            <div className="rounded-xl border-2 border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3">
              <div className="flex items-start gap-2">
                <ShieldAlert className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    Found a larger list with {bestFound.follows.length} follows!
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    From {formatDate(bestFound.createdAt)} — that's {bestFound.follows.length - currentCount!} more than your current list
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleRestore(bestFound)}
                disabled={restoring}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 text-white font-medium text-sm hover:bg-emerald-600 disabled:opacity-50 transition-colors"
              >
                {restoring ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Restoring...</>
                ) : (
                  <><RotateCcw className="w-4 h-4" /> Restore {bestFound.follows.length} Follows</>
                )}
              </button>
            </div>
          )}

          {/* No better list found */}
          {!hasBetterList && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border">
              <Check className="w-4 h-4 text-emerald-500 shrink-0" />
              <p className="text-sm text-muted-foreground">
                Your current follow list appears to be the most complete version across all relays.
              </p>
            </div>
          )}

          {/* All found lists (collapsible) */}
          {foundLists.length > 1 && (
            <FoundListsDetail
              lists={foundLists}
              currentCount={currentCount!}
              onRestore={handleRestore}
              restoring={restoring}
              formatDate={formatDate}
            />
          )}

          {/* Scan again */}
          <button
            onClick={handleScan}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Scan again
          </button>
        </div>
      )}

      {/* Restored success */}
      {restored && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <Check className="w-5 h-5 text-emerald-500 shrink-0" />
          <div>
            <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">Follow list restored!</p>
            <p className="text-xs text-muted-foreground">
              Your contact list has been republished with {currentCount} follows. It may take a minute for all relays to update.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
          <p className="text-xs text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
}

function FoundListsDetail({ lists, currentCount, onRestore, restoring, formatDate }: {
  lists: FoundContactList[];
  currentCount: number;
  onRestore: (list: FoundContactList) => void;
  restoring: boolean;
  formatDate: (ts: number) => string;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-xs text-primary hover:underline"
      >
        {expanded ? 'Hide' : 'Show'} all {lists.length} versions found
      </button>

      {expanded && (
        <div className="mt-2 space-y-1.5 max-h-60 overflow-y-auto">
          {lists.map((list, i) => (
            <div
              key={list.eventId}
              className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-xs ${
                list.follows.length > currentCount
                  ? 'border-emerald-500/20 bg-emerald-500/5'
                  : 'border-border bg-muted/30'
              }`}
            >
              <div className="min-w-0">
                <p className="font-medium">
                  {list.follows.length} follows
                  {list.follows.length > currentCount && (
                    <span className="text-emerald-500 ml-1">(+{list.follows.length - currentCount})</span>
                  )}
                  {list.follows.length === currentCount && (
                    <span className="text-muted-foreground ml-1">(current)</span>
                  )}
                </p>
                <p className="text-muted-foreground truncate">
                  {formatDate(list.createdAt)} · {list.relayUrl.replace('wss://', '')}
                </p>
              </div>
              {list.follows.length > currentCount && (
                <button
                  onClick={() => onRestore(list)}
                  disabled={restoring}
                  className="shrink-0 px-2.5 py-1 rounded-lg bg-emerald-500 text-white font-medium hover:bg-emerald-600 disabled:opacity-50 transition-colors"
                >
                  Restore
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── NIP-05 Claim Section ──────────────────────────────────────────
// Lets users claim username@bchnostr.com for $1 in BCH.
// Publishes a kind 30078 event with d-tag "bchnostr/nip05/<name>"
// that the Netlify function reads to resolve NIP-05 lookups.

const NIP05_CLAIM_KIND = 30078;
const NIP05_D_TAG_PREFIX = 'bchnostr/nip05/';
const NIP05_CLAIM_FEE_USD = 1;
const NIP05_DOMAIN = 'bchnostr.com';
const NIP05_VALID_CHARS = /^[a-z0-9_.-]+$/;

function Nip05ClaimSection({ currentNip05, pubkey, onClaimed }: {
  currentNip05: string;
  pubkey: string;
  onClaimed: (nip05: string) => void;
}) {
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const [desiredName, setDesiredName] = useState('');
  const [step, setStep] = useState<'idle' | 'confirm' | 'paying' | 'publishing' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [walletInitializing, setWalletInitializing] = useState(false);
  const [checking, setChecking] = useState(false);
  const [nameAvailable, setNameAvailable] = useState<boolean | null>(null);
  const checkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const alreadyHasBchnostrNip05 = currentNip05?.endsWith('@' + NIP05_DOMAIN);
  const currentClaimedName = alreadyHasBchnostrNip05
    ? currentNip05.replace('@' + NIP05_DOMAIN, '')
    : null;

  const price = getBCHPrice();
  const feeSats = price ? Math.round((NIP05_CLAIM_FEE_USD / price) * 1e8) : 0;
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = walletBalance >= feeSats + 300;

  const normalizedName = desiredName.trim().toLowerCase();
  const isValidName = normalizedName.length >= 1 && normalizedName.length <= 30 && NIP05_VALID_CHARS.test(normalizedName);

  // Debounced availability check
  useEffect(() => {
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    setNameAvailable(null);

    if (!normalizedName || !isValidName) return;
    // Don't check if it's their current name
    if (normalizedName === currentClaimedName) {
      setNameAvailable(true);
      return;
    }

    setChecking(true);
    checkTimerRef.current = setTimeout(async () => {
      try {
        const result = await checkNameAvailability(normalizedName, pubkey);
        // 'unknown' means the directory is unreachable — don't block the
        // user on our own outage; the claim is validated server-side.
        setNameAvailable(result !== 'taken');
      } catch {
        setNameAvailable(true);
      } finally {
        setChecking(false);
      }
    }, 500);

    return () => { if (checkTimerRef.current) clearTimeout(checkTimerRef.current); };
  }, [normalizedName, isValidName, pubkey, currentClaimedName]);

  const handleInitWallet = async () => {
    setWalletInitializing(true);
    try { await tipWallet.init(); } catch {}
    setWalletInitializing(false);
  };

  const handleClaim = async () => {
    if (!isValidName || !nameAvailable) return;
    setStep('paying');
    setError(null);

    try {
      // 1. Make sure wallet is ready
      if (!tipWallet.ready) await tipWallet.init();

      // 2. Pay the fee. Keep the txid — it's the only thing that makes
      //    the claim auditable on-chain later.
      const txId = await tipWallet.sendBCH(PLATFORM_FEE_ADDRESS, BigInt(feeSats));

      // 3. Publish claim event. The `fee` tag records the payment so an
      //    operator can verify it against the chain; without it a claim
      //    event is just an unbacked assertion that someone paid.
      setStep('publishing');
      const now = Math.floor(Date.now() / 1000);
      await publish({
        kind: NIP05_CLAIM_KIND,
        content: JSON.stringify({
          name: normalizedName,
          domain: NIP05_DOMAIN,
          claimedAt: now,
          txid: txId || undefined,
          amountSats: feeSats,
          feeAddress: PLATFORM_FEE_ADDRESS,
        }),
        tags: [
          ['d', NIP05_D_TAG_PREFIX + normalizedName],
          ['t', 'bch-nip05'],
          ['alt', `NIP-05 claim: ${normalizedName}@${NIP05_DOMAIN}`],
          ...(txId ? [['fee', txId, String(feeSats), PLATFORM_FEE_ADDRESS]] : []),
        ],
        created_at: now,
      });

      // 4. Update the nip05 field
      const newNip05 = `${normalizedName}@${NIP05_DOMAIN}`;
      // Invalidate the NIP-05 verification cache so the badge shows immediately
      invalidateNip05Cache(newNip05, pubkey);
      onClaimed(newNip05);

      tipWallet.refreshBalance();
      setStep('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Claim failed');
      setStep('error');
    }
  };

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <BadgeCheck className="w-5 h-5 text-primary" />
        <div>
          <h3 className="text-sm font-bold">Get Verified on BCH Nostr</h3>
          <p className="text-[10px] text-muted-foreground">Claim your <strong>username@{NIP05_DOMAIN}</strong> identifier — $1 in BCH</p>
        </div>
      </div>

      {alreadyHasBchnostrNip05 && step !== 'done' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <BadgeCheck className="w-4 h-4 text-emerald-500" />
          <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
            You're verified as <strong>{currentNip05}</strong>
          </span>
        </div>
      )}

      {step === 'done' ? (
        <div className="text-center py-4 space-y-2">
          <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
            <BadgeCheck className="w-6 h-6 text-emerald-500" />
          </div>
          <p className="text-sm font-bold text-emerald-500">You're verified!</p>
          <p className="text-xs text-muted-foreground">
            <strong>{normalizedName}@{NIP05_DOMAIN}</strong> is now yours. Your profile has been updated across Nostr.
          </p>
        </div>
      ) : (
        <>
          {/* Username input */}
          <div>
            <div className="flex items-center gap-0">
              <input
                value={desiredName}
                onChange={e => { setDesiredName(e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, '')); setStep('idle'); }}
                placeholder="yourname"
                maxLength={30}
                className="flex-1 bg-muted rounded-l-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <span className="bg-muted/60 border-l border-border px-3 py-2.5 text-sm text-muted-foreground rounded-r-lg font-mono">
                @{NIP05_DOMAIN}
              </span>
            </div>

            {/* Availability indicator */}
            {normalizedName && isValidName && (
              <div className="mt-1.5 flex items-center gap-1.5">
                {checking ? (
                  <><Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /><span className="text-[10px] text-muted-foreground">Checking...</span></>
                ) : nameAvailable === true ? (
                  <><Check className="w-3 h-3 text-emerald-500" /><span className="text-[10px] text-emerald-500 font-medium">{normalizedName === currentClaimedName ? 'Already yours' : 'Available!'}</span></>
                ) : nameAvailable === false ? (
                  <><AlertTriangle className="w-3 h-3 text-red-500" /><span className="text-[10px] text-red-500 font-medium">Taken — try another name</span></>
                ) : null}
              </div>
            )}
            {normalizedName && !isValidName && (
              <p className="text-[10px] text-red-500 mt-1">Only lowercase letters, numbers, dots, hyphens, and underscores</p>
            )}
          </div>

          {/* Pricing */}
          {isValidName && nameAvailable && normalizedName !== currentClaimedName && (
            <div className="rounded-lg bg-muted/50 border border-border p-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Verification fee</span>
                <span className="font-bold font-mono">${NIP05_CLAIM_FEE_USD}{feeSats > 0 ? ` (${feeSats.toLocaleString()} sats)` : ''}</span>
              </div>

              {/* Wallet */}
              {!tipWallet.ready ? (
                <button
                  onClick={handleInitWallet}
                  disabled={walletInitializing}
                  className="w-full py-2 rounded-lg bg-muted text-xs font-medium flex items-center justify-center gap-1.5"
                >
                  {walletInitializing
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Wallet className="w-3.5 h-3.5" />}
                  {walletInitializing ? 'Loading...' : 'Load Wallet'}
                </button>
              ) : (
                <>
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>Wallet balance</span>
                    <span className={`font-mono font-bold ${canAfford ? 'text-emerald-500' : 'text-red-500'}`}>
                      {walletBalance.toLocaleString()} sats
                    </span>
                  </div>
                  <button
                    onClick={handleClaim}
                    disabled={step === 'paying' || step === 'publishing' || !canAfford}
                    className={`w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                      canAfford
                        ? 'bg-gradient-to-r from-primary to-emerald-500 text-white hover:opacity-90'
                        : 'bg-muted text-muted-foreground cursor-not-allowed'
                    } disabled:opacity-50`}
                  >
                    {step === 'paying' ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Paying...</>
                    ) : step === 'publishing' ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Claiming...</>
                    ) : (
                      <><BadgeCheck className="w-4 h-4" /> Claim for ${NIP05_CLAIM_FEE_USD}</>
                    )}
                  </button>
                  {!canAfford && (
                    <p className="text-[10px] text-red-500">Not enough funds. Deposit to your tip wallet first.</p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Error */}
          {step === 'error' && error && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3">
              <p className="text-xs text-red-500">{error}</p>
              <button onClick={() => setStep('idle')} className="text-xs text-primary hover:underline mt-1">Try again</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Business gallery uploader (multi-image) ──────────────────────
const MAX_GALLERY_IMAGES = 8;

function BusinessGalleryField({ images, onChange }: { images: string[]; onChange: (imgs: string[]) => void }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const atLimit = images.length >= MAX_GALLERY_IMAGES;

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    setError(null);
    setUploading(true);
    const added: string[] = [];
    try {
      for (const file of files) {
        if (images.length + added.length >= MAX_GALLERY_IMAGES) break;
        setProgress(0);
        const url = await uploadImage(file, setProgress);
        added.push(url);
      }
      if (added.length > 0) onChange([...images, ...added]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      if (added.length > 0) onChange([...images, ...added]);
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const removeAt = (i: number) => onChange(images.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="text-[11px] font-medium text-muted-foreground">
        Photo Gallery <span className="text-muted-foreground/60">({images.length}/{MAX_GALLERY_IMAGES})</span>
      </label>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1">
        {images.map((url, i) => (
          <div key={`${url}-${i}`} className="relative aspect-square rounded-lg overflow-hidden border border-border group">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        {!atLimit && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="aspect-square rounded-lg border border-dashed border-border bg-muted flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors disabled:opacity-50"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                <span className="text-[9px]">{progress}%</span>
              </>
            ) : (
              <>
                <Camera className="w-5 h-5" />
                <span className="text-[9px] font-medium">Add</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,image/heic,image/heif"
        multiple
        className="hidden"
        onChange={handleFiles}
      />
      {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function ImageUploadField({ label, value, onChange, preview }: { label: string; value: string; onChange: (url: string) => void; preview: 'avatar' | 'banner' }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setUploading(true);
    setError(null);
    setProgress(0);
    try {
      const url = await uploadImage(file, setProgress);
      onChange(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <div className="mt-1 space-y-2">
        {/* Preview + Upload button */}
        <div className="flex items-center gap-3">
          {preview === 'avatar' ? (
            <div className="shrink-0">
              {value ? (
                <img src={value} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-border" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
                  <Camera className="w-6 h-6 text-muted-foreground/40" />
                </div>
              )}
            </div>
          ) : (
            <div className="shrink-0">
              {value ? (
                <img src={value} alt="" className="w-24 h-12 rounded-lg object-cover border border-border" />
              ) : (
                <div className="w-24 h-12 rounded-lg bg-muted flex items-center justify-center border border-dashed border-border">
                  <Camera className="w-5 h-5 text-muted-foreground/40" />
                </div>
              )}
            </div>
          )}
          <div className="flex-1 space-y-1.5">
            <input type="hidden" />
            <input
              ref={fileRef}
              type="file"
              accept="image/*,image/heic,image/heif"
              className="hidden"
              onChange={handleFile}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-50"
            >
              {uploading ? (
                <><div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Uploading {progress}%</>
              ) : (
                <><Camera className="w-3.5 h-3.5" /> Upload {label}</>
              )}
            </button>
            {value && (
              <button onClick={() => onChange('')} className="text-[10px] text-red-500 hover:underline">Remove</button>
            )}
          </div>
        </div>

        {/* Upload progress */}
        {uploading && (
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        )}

        {/* Error */}
        {error && <p className="text-[10px] text-red-500">{error}</p>}

        {/* URL fallback */}
        <div>
          <p className="text-[10px] text-muted-foreground mb-0.5">Or paste a URL:</p>
          <input value={value} onChange={e => onChange(e.target.value)} placeholder="https://..." className="w-full bg-muted rounded-lg px-3 py-1.5 text-xs mt-0.5 focus:outline-none focus:ring-1 focus:ring-primary font-mono" />
        </div>
      </div>
    </div>
  );
}

// Backup for the active Business Page's app-held key. Businesses are the
// one identity whose private key the app holds, so losing it (cleared
// browser, new device) loses the Page and its funds — this makes the nsec
// recoverable on demand, behind a reveal.
function BusinessKeyBackup({ pubkey }: { pubkey: string }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const nsec = revealed ? exportBusinessNsec(pubkey) : null;

  const copy = async () => {
    if (!nsec) return;
    try {
      await navigator.clipboard.writeText(nsec);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="rounded-2xl border-2 border-amber-500/40 bg-amber-500/[0.06] p-4 space-y-2">
      <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
        <ShieldAlert className="w-4 h-4 shrink-0" />
        <h3 className="text-sm font-bold">Back up this Business Page</h3>
      </div>
      <p className="text-[11px] text-muted-foreground leading-relaxed">
        This Page's private key lives only in this browser. Save it somewhere safe — without it,
        clearing your data or switching devices loses the Page and any BCH it holds.
      </p>
      {!revealed ? (
        <button
          onClick={() => setRevealed(true)}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-muted text-xs font-semibold hover:bg-muted/70"
        >
          <Eye className="w-3.5 h-3.5" /> Reveal key
        </button>
      ) : nsec ? (
        <div className="flex items-center gap-2">
          <code className="flex-1 min-w-0 break-all text-[10px] font-mono bg-background rounded-lg px-2.5 py-2 border border-border">
            {nsec}
          </code>
          <button
            onClick={copy}
            className="shrink-0 px-2.5 py-2 rounded-lg bg-muted text-[11px] font-medium hover:bg-muted/70 flex items-center gap-1"
          >
            {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
          </button>
        </div>
      ) : (
        <p className="text-[11px] text-red-500">Key not found in this browser.</p>
      )}
    </div>
  );
}

// ── Security tab ──────────────────────────────────────────────────
//
// One home for at-rest PIN encryption of the two secrets this app keeps in
// the browser: the Nostr key (nsec) and the BCH tip-wallet key (WIF). Each
// sub-section explains itself even when it can't be used (extension login /
// no wallet yet) so the feature is always discoverable.
function SecuritySettings({ user }: { user: any }) {
  const tipWallet = useTipWallet();
  const initCalledRef = useRef(false);

  // Make sure the wallet has attempted to load so `hasWallet` / `locked` /
  // `encrypted` reflect reality when the user opens this tab directly.
  useEffect(() => {
    if (initCalledRef.current) return;
    initCalledRef.current = true;
    tipWallet.init().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4">
      {/* Intro */}
      <div className="glass-card">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-2">
          <ShieldCheck className="w-4 h-4 text-primary" /> Security
        </h3>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Your Nostr key and BCH wallet key are stored in this browser. By
          default they're saved unencrypted for convenience. Set a PIN to
          encrypt them at rest — a stolen browser copy is then useless without
          it. You'll enter the PIN once per session.
        </p>
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 p-3">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            A PIN <strong className="text-foreground">cannot be recovered</strong>.
            If you forget it, you'll need your nsec / WIF backup to restore
            access. This protects your keys at rest — it is not a substitute for
            keeping those backups somewhere safe.
          </p>
        </div>
      </div>

      {/* Nostr key (nsec) PIN */}
      <KeyEncryptionSection loginMethod={user.loginMethod} />

      {/* BCH wallet key (WIF) PIN */}
      <WalletEncryptionSection tipWallet={tipWallet} variant="full" />
    </div>
  );
}

// ── PIN encryption for the locally-stored key ─────────────────────
//
// Only meaningful for the nsec login method: extension / bunker signers
// never expose a key to us, and business Pages hold their own app key. When
// enabled, the nsec is stored as an AES-GCM envelope instead of plaintext,
// so a stolen localStorage dump is useless without the PIN.
function KeyEncryptionSection({ loginMethod }: { loginMethod?: string }) {
  const [encrypted, setEncrypted] = useState(() => isNsecEncrypted());
  const [mode, setMode] = useState<'idle' | 'enable' | 'disable' | 'change'>('idle');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [oldPin, setOldPin] = useState('');
  const [ack, setAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  // Only nsec logins have a local key we can encrypt. For extension / bunker
  // logins there's no key on this device, so explain rather than hide.
  if (loginMethod !== 'nsec') {
    return (
      <div className="mt-4 rounded-xl border border-border bg-card p-4">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Key className="w-4 h-4 text-muted-foreground" />
          Encrypt your Nostr key with a PIN
        </h3>
        <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
          {loginMethod === 'extension'
            ? 'You logged in with a browser extension (nos2x, Alby, etc.). Your Nostr key is held by the extension, not this app — manage its security there. There is nothing stored on this device to encrypt.'
            : loginMethod === 'bunker' || loginMethod === 'remote'
              ? 'You logged in with a remote signer (bunker). Your Nostr key never touches this device, so there is nothing stored here to encrypt.'
              : 'Your Nostr key is not stored on this device, so there is nothing here to encrypt.'}
        </p>
      </div>
    );
  }

  const reset = () => {
    setMode('idle'); setPin(''); setPin2(''); setOldPin('');
    setAck(false); setError(null); setBusy(false);
  };

  const flash = (msg: string) => { setDone(msg); setTimeout(() => setDone(null), 3000); };

  const handleEnable = async () => {
    setError(null);
    const v = validatePin(pin);
    if (v) { setError(v); return; }
    if (pin !== pin2) { setError('PINs don’t match.'); return; }
    if (!ack) { setError('Please confirm you understand a lost PIN can’t be recovered.'); return; }
    const nsec = loadStoredNsec();
    if (!nsec) { setError('Your key isn’t available. Try reloading.'); return; }
    setBusy(true);
    try {
      await enableNsecEncryption(nsec, pin);
      setEncrypted(true);
      reset();
      flash('PIN enabled — your key is now encrypted on this device.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not enable encryption.');
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setError(null);
    if (!oldPin) { setError('Enter your current PIN.'); return; }
    setBusy(true);
    try {
      await disableNsecEncryption(oldPin);
      setEncrypted(false);
      reset();
      flash('PIN removed — your key is stored unencrypted again.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Incorrect PIN.');
      setBusy(false);
    }
  };

  const handleChange = async () => {
    setError(null);
    const v = validatePin(pin);
    if (v) { setError(v); return; }
    if (pin !== pin2) { setError('New PINs don’t match.'); return; }
    setBusy(true);
    try {
      await changeNsecPin(oldPin, pin);
      reset();
      flash('PIN changed.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Incorrect current PIN.');
      setBusy(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div className="mt-4 rounded-xl border border-border bg-card p-4">
      <h3 className="font-semibold text-sm flex items-center gap-2">
        <Key className="w-4 h-4 text-primary" />
        Encrypt your key with a PIN
        {encrypted && (
          <span className="ml-auto flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
            <Check className="w-3 h-3" /> On
          </span>
        )}
      </h3>
      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
        Your Nostr key is stored on this device. By default it's saved in plain
        text — a PIN encrypts it so a stolen browser copy is useless without it.
        You'll enter the PIN once each time you open the app.
      </p>

      {done && (
        <p className="mt-2 rounded-lg bg-emerald-500/10 px-3 py-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          {done}
        </p>
      )}

      {/* Idle: show the primary action */}
      {mode === 'idle' && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!encrypted ? (
            <button
              onClick={() => setMode('enable')}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-primary-foreground hover:bg-primary/90"
            >
              <Key className="w-3.5 h-3.5" /> Set a PIN
            </button>
          ) : (
            <>
              <button
                onClick={() => setMode('change')}
                className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Change PIN
              </button>
              <button
                onClick={() => setMode('disable')}
                className="flex items-center gap-1.5 rounded-lg border border-red-500/40 px-3 py-2 text-xs font-semibold text-red-500 hover:bg-red-500/5"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove PIN
              </button>
            </>
          )}
        </div>
      )}

      {/* Enable */}
      {mode === 'enable' && (
        <div className="mt-3 space-y-2.5">
          <input type="password" value={pin} onChange={e => { setPin(e.target.value); setError(null); }}
            placeholder="Choose a PIN or password" autoComplete="new-password" className={inputCls} />
          <input type="password" value={pin2} onChange={e => { setPin2(e.target.value); setError(null); }}
            placeholder="Confirm PIN" autoComplete="new-password" className={inputCls}
            onKeyDown={e => { if (e.key === 'Enter' && !busy) handleEnable(); }} />
          <label className="flex items-start gap-2 text-[11px] text-muted-foreground leading-relaxed">
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} className="mt-0.5" />
            <span>
              I understand this PIN <strong className="text-foreground">cannot be recovered</strong>. If I
              forget it, I'll need to log back in with my nsec backup.
            </span>
          </label>
          {error && <p className="text-xs font-medium text-red-500">{error}</p>}
          <div className="flex gap-2">
            <button onClick={handleEnable} disabled={busy}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Key className="w-3.5 h-3.5" />}
              {busy ? 'Encrypting…' : 'Enable'}
            </button>
            <button onClick={reset} disabled={busy} className="rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Change */}
      {mode === 'change' && (
        <div className="mt-3 space-y-2.5">
          <input type="password" value={oldPin} onChange={e => { setOldPin(e.target.value); setError(null); }}
            placeholder="Current PIN" autoComplete="off" className={inputCls} />
          <input type="password" value={pin} onChange={e => { setPin(e.target.value); setError(null); }}
            placeholder="New PIN" autoComplete="new-password" className={inputCls} />
          <input type="password" value={pin2} onChange={e => { setPin2(e.target.value); setError(null); }}
            placeholder="Confirm new PIN" autoComplete="new-password" className={inputCls}
            onKeyDown={e => { if (e.key === 'Enter' && !busy) handleChange(); }} />
          {error && <p className="text-xs font-medium text-red-500">{error}</p>}
          <div className="flex gap-2">
            <button onClick={handleChange} disabled={busy}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {busy ? 'Updating…' : 'Change PIN'}
            </button>
            <button onClick={reset} disabled={busy} className="rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Disable */}
      {mode === 'disable' && (
        <div className="mt-3 space-y-2.5">
          <p className="text-xs text-muted-foreground">
            Removing the PIN stores your key in plain text again. Enter your PIN to confirm.
          </p>
          <input type="password" value={oldPin} onChange={e => { setOldPin(e.target.value); setError(null); }}
            placeholder="Current PIN" autoComplete="off" className={inputCls}
            onKeyDown={e => { if (e.key === 'Enter' && !busy) handleDisable(); }} />
          {error && <p className="text-xs font-medium text-red-500">{error}</p>}
          <div className="flex gap-2">
            <button onClick={handleDisable} disabled={busy}
              className="flex items-center gap-1.5 rounded-lg bg-red-500 px-3 py-2 text-xs font-bold text-white hover:bg-red-600 disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {busy ? 'Removing…' : 'Remove PIN'}
            </button>
            <button onClick={reset} disabled={busy} className="rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── PIN encryption for the BCH tip-wallet WIF ─────────────────────
//
// By default the WIF (which controls the BCH funds) sits in localStorage in
// plaintext. This lets the user OPT IN to encrypting it behind a PIN — the
// stored value becomes an AES-GCM envelope that's useless without the PIN.
// After enabling, the WIF must be unlocked once per session before the wallet
// can spend. Same crypto and UX as the nsec encryption above.
//
// `variant`:
//   · 'full'        — the Security tab: manage enable / change / remove + unlock.
//   · 'unlock-only' — the Wallet tab: only surface the unlock prompt when the
//                     wallet is locked (so the user can regain access where
//                     they're trying to spend); management lives in Security.
function WalletEncryptionSection({
  tipWallet,
  variant = 'full',
}: {
  tipWallet: TipWallet;
  variant?: 'full' | 'unlock-only';
}) {
  const [mode, setMode] = useState<'idle' | 'enable' | 'disable' | 'change'>('idle');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [oldPin, setOldPin] = useState('');
  const [ack, setAck] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [unlockPin, setUnlockPin] = useState('');
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [unlocking, setUnlocking] = useState(false);

  // In the Wallet tab we only ever surface the unlock prompt; management of
  // the PIN lives in the Security tab. When there's nothing to unlock, render
  // nothing so the Wallet tab stays uncluttered.
  if (variant === 'unlock-only' && !tipWallet.locked) return null;

  const reset = () => {
    setMode('idle'); setPin(''); setPin2(''); setOldPin('');
    setAck(false); setError(null); setBusy(false);
  };

  const flash = (msg: string) => { setDone(msg); setTimeout(() => setDone(null), 3000); };

  const handleEnable = async () => {
    setError(null);
    const v = validatePin(pin);
    if (v) { setError(v); return; }
    if (pin !== pin2) { setError('PINs don’t match.'); return; }
    if (!ack) { setError('Please confirm you understand a lost PIN can’t be recovered.'); return; }
    setBusy(true);
    try {
      await tipWallet.encryptWallet(pin);
      reset();
      flash('PIN enabled — your wallet key is now encrypted on this device.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not enable encryption.');
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setError(null);
    if (!oldPin) { setError('Enter your current PIN.'); return; }
    setBusy(true);
    try {
      await tipWallet.decryptWallet(oldPin);
      reset();
      flash('PIN removed — your wallet key is stored unencrypted again.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Incorrect PIN.');
      setBusy(false);
    }
  };

  const handleChange = async () => {
    setError(null);
    const v = validatePin(pin);
    if (v) { setError(v); return; }
    if (pin !== pin2) { setError('New PINs don’t match.'); return; }
    setBusy(true);
    try {
      await tipWallet.changeWalletPin(oldPin, pin);
      reset();
      flash('PIN changed.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Incorrect current PIN.');
      setBusy(false);
    }
  };

  const handleUnlock = async () => {
    setUnlockError(null);
    if (!unlockPin) { setUnlockError('Enter your PIN.'); return; }
    setUnlocking(true);
    try {
      await tipWallet.unlockWallet(unlockPin);
      setUnlockPin('');
    } catch (e) {
      setUnlockError(e instanceof Error ? e.message : 'Incorrect PIN.');
    } finally {
      setUnlocking(false);
    }
  };

  const inputCls = 'w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40';
  const encrypted = tipWallet.encrypted;

  // ── Locked state: the WIF is encrypted and hasn't been unlocked ──
  if (tipWallet.locked) {
    return (
      <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/[0.06] p-4">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Lock className="w-4 h-4 text-amber-500" />
          Wallet locked
        </h3>
        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
          Your wallet key is encrypted with a PIN. Enter it to unlock and use your BCH tip wallet this session.
        </p>
        <div className="mt-3 space-y-2.5">
          <input
            type="password"
            value={unlockPin}
            onChange={e => { setUnlockPin(e.target.value); setUnlockError(null); }}
            placeholder="Enter your PIN"
            autoComplete="off"
            className={inputCls}
            onKeyDown={e => { if (e.key === 'Enter' && !unlocking) handleUnlock(); }}
          />
          {unlockError && <p className="text-xs font-medium text-red-500">{unlockError}</p>}
          <button
            onClick={handleUnlock}
            disabled={unlocking}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {unlocking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
            {unlocking ? 'Unlocking…' : 'Unlock Wallet'}
          </button>
        </div>
      </div>
    );
  }

  // unlock-only variant: past this point there's nothing to show (not locked).
  if (variant === 'unlock-only') return null;

  // No wallet yet — explain instead of hiding (Security tab only).
  if (!tipWallet.hasWallet) {
    return (
      <div className="mt-4 rounded-xl border border-border bg-card p-4">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-muted-foreground" />
          Encrypt your wallet key with a PIN
        </h3>
        <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
          You don't have a BCH tip wallet on this device yet. Create or import
          one first — then you can protect its key with a PIN here.
        </p>
        <Link
          to="/settings?tab=wallet"
          className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted"
        >
          <Wallet className="w-3.5 h-3.5" /> Go to Wallet
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-border bg-card p-4">
      <h3 className="font-semibold text-sm flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-primary" />
        Encrypt your wallet key with a PIN
        {encrypted && (
          <span className="ml-auto flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
            <Check className="w-3 h-3" /> On
          </span>
        )}
      </h3>
      <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
        Your BCH wallet key (WIF) controls your funds and is stored on this
        device. By default it's saved in plain text — a PIN encrypts it so a
        stolen browser copy is useless without it. You'll enter the PIN once
        each time you open the app.
      </p>

      {done && (
        <p className="mt-2 rounded-lg bg-emerald-500/10 px-3 py-2 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          {done}
        </p>
      )}

      {/* Idle */}
      {mode === 'idle' && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!encrypted ? (
            <button
              onClick={() => setMode('enable')}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-primary-foreground hover:bg-primary/90"
            >
              <Lock className="w-3.5 h-3.5" /> Set a PIN
            </button>
          ) : (
            <>
              <button
                onClick={() => setMode('change')}
                className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Change PIN
              </button>
              <button
                onClick={() => setMode('disable')}
                className="flex items-center gap-1.5 rounded-lg border border-red-500/40 px-3 py-2 text-xs font-semibold text-red-500 hover:bg-red-500/5"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove PIN
              </button>
            </>
          )}
        </div>
      )}

      {/* Enable */}
      {mode === 'enable' && (
        <div className="mt-3 space-y-2.5">
          <input type="password" value={pin} onChange={e => { setPin(e.target.value); setError(null); }}
            placeholder="Choose a PIN or password" autoComplete="new-password" className={inputCls} />
          <input type="password" value={pin2} onChange={e => { setPin2(e.target.value); setError(null); }}
            placeholder="Confirm PIN" autoComplete="new-password" className={inputCls}
            onKeyDown={e => { if (e.key === 'Enter' && !busy) handleEnable(); }} />
          <label className="flex items-start gap-2 text-[11px] text-muted-foreground leading-relaxed">
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} className="mt-0.5" />
            <span>
              I understand this PIN <strong className="text-foreground">cannot be recovered</strong>. If I
              forget it, I'll need my WIF backup to restore the wallet.
            </span>
          </label>
          {error && <p className="text-xs font-medium text-red-500">{error}</p>}
          <div className="flex gap-2">
            <button onClick={handleEnable} disabled={busy}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
              {busy ? 'Encrypting…' : 'Enable'}
            </button>
            <button onClick={reset} disabled={busy} className="rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Change */}
      {mode === 'change' && (
        <div className="mt-3 space-y-2.5">
          <input type="password" value={oldPin} onChange={e => { setOldPin(e.target.value); setError(null); }}
            placeholder="Current PIN" autoComplete="off" className={inputCls} />
          <input type="password" value={pin} onChange={e => { setPin(e.target.value); setError(null); }}
            placeholder="New PIN" autoComplete="new-password" className={inputCls} />
          <input type="password" value={pin2} onChange={e => { setPin2(e.target.value); setError(null); }}
            placeholder="Confirm new PIN" autoComplete="new-password" className={inputCls}
            onKeyDown={e => { if (e.key === 'Enter' && !busy) handleChange(); }} />
          {error && <p className="text-xs font-medium text-red-500">{error}</p>}
          <div className="flex gap-2">
            <button onClick={handleChange} disabled={busy}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {busy ? 'Updating…' : 'Change PIN'}
            </button>
            <button onClick={reset} disabled={busy} className="rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Disable */}
      {mode === 'disable' && (
        <div className="mt-3 space-y-2.5">
          <p className="text-xs text-muted-foreground">
            Removing the PIN stores your wallet key in plain text again. Enter your PIN to confirm.
          </p>
          <input type="password" value={oldPin} onChange={e => { setOldPin(e.target.value); setError(null); }}
            placeholder="Current PIN" autoComplete="off" className={inputCls}
            onKeyDown={e => { if (e.key === 'Enter' && !busy) handleDisable(); }} />
          {error && <p className="text-xs font-medium text-red-500">{error}</p>}
          <div className="flex gap-2">
            <button onClick={handleDisable} disabled={busy}
              className="flex items-center gap-1.5 rounded-lg bg-red-500 px-3 py-2 text-xs font-bold text-white hover:bg-red-600 disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {busy ? 'Removing…' : 'Remove PIN'}
            </button>
            <button onClick={reset} disabled={busy} className="rounded-lg border border-border px-3 py-2 text-xs font-semibold hover:bg-muted">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function YourKeys({ pubkey, loginMethod }: { pubkey: string; loginMethod?: string }) {
  const [showNsec, setShowNsec] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const npub = nip19.npubEncode(pubkey);
  const storedNsec = loadStoredNsec();

  const copyText = (text: string, field: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="mt-6 pt-6 border-t border-border space-y-4">
      <h3 className="font-semibold text-sm flex items-center gap-2">
        <Key className="w-4 h-4 text-primary" /> Your Nostr Keys
      </h3>

      {/* Public Key */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-muted-foreground">Public Key (npub) — safe to share</label>
          <button onClick={() => copyText(npub, 'npub')} className="text-[10px] text-primary hover:underline flex items-center gap-0.5">
            {copiedField === 'npub' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
          </button>
        </div>
        <div className="bg-muted rounded-lg p-2.5">
          <code className="text-[10px] font-mono break-all">{npub}</code>
        </div>
      </div>

      {/* Hex Pubkey */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-xs font-medium text-muted-foreground">Hex Public Key</label>
          <button onClick={() => copyText(pubkey, 'hex')} className="text-[10px] text-primary hover:underline flex items-center gap-0.5">
            {copiedField === 'hex' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
          </button>
        </div>
        <div className="bg-muted rounded-lg p-2.5">
          <code className="text-[10px] font-mono break-all">{pubkey}</code>
        </div>
      </div>

      {/* Private Key (nsec) — only if logged in via nsec */}
      {storedNsec ? (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-medium text-red-500">Private Key (nsec) — KEEP SECRET</label>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowNsec(!showNsec)} className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-0.5">
                {showNsec ? <><EyeOff className="w-3 h-3" /> Hide</> : <><Eye className="w-3 h-3" /> Reveal</>}
              </button>
              {showNsec && (
                <button onClick={() => copyText(storedNsec, 'nsec')} className="text-[10px] text-primary hover:underline flex items-center gap-0.5">
                  {copiedField === 'nsec' ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              )}
            </div>
          </div>
          <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-2.5">
            <code className="text-[10px] font-mono break-all">
              {showNsec ? storedNsec : '\u{2022}'.repeat(63)}
            </code>
          </div>
          <div className="flex items-start gap-2 mt-2 p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-[10px] text-muted-foreground">
              This is your Nostr private key. Anyone who has it can control your account. Save it somewhere safe — a password manager, written down, or encrypted backup. If you lose it, your account is gone forever.
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-lg bg-muted/50 p-3">
          <p className="text-xs text-muted-foreground">
            {loginMethod === 'extension'
              ? 'You logged in with a browser extension. Your private key is managed by the extension (nos2x, Alby, etc.) — check there to export it.'
              : 'No private key stored in this browser.'}
          </p>
        </div>
      )}
    </div>
  );
}

// Relays that BCHNostr depends on — missing write access causes posts
// to be invisible to other BCHNostr users or common Nostr clients.
const CRITICAL_RELAYS = [
  {
    url: 'wss://relay.bchnostr.com',
    label: 'BCHNostr Relay',
    reason: 'The main relay for BCHNostr. Without write access, your posts won\'t be visible to other BCHNostr users.',
  },
  {
    url: 'wss://nos.lol',
    label: 'nos.lol',
    reason: 'High-availability relay used by many Nostr clients.',
  },
];

type RelayStatus = 'checking' | 'online' | 'offline' | 'unknown';

function testRelayConnection(url: string): Promise<RelayStatus> {
  return new Promise((resolve) => {
    try {
      const ws = new WebSocket(url);
      const timer = setTimeout(() => { ws.close(); resolve('offline'); }, 5000);
      ws.onopen = () => { clearTimeout(timer); ws.close(); resolve('online'); };
      ws.onerror = () => { clearTimeout(timer); resolve('offline'); };
    } catch {
      resolve('offline');
    }
  });
}

function RelaySettings() {
  const [relays, setRelays] = useState<{ url: string; read: boolean; write: boolean }[]>([]);
  const [newRelay, setNewRelay] = useState('');
  const [statuses, setStatuses] = useState<Record<string, RelayStatus>>({});
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    try {
      const cfg = JSON.parse(localStorage.getItem('nostr:app-config') || '{}');
      setRelays(cfg.relayMetadata?.relays || []);
    } catch(e) {}
  }, []);

  const saveRelays = (updated: typeof relays) => {
    setRelays(updated);
    const cfg = JSON.parse(localStorage.getItem('nostr:app-config') || '{}');
    cfg.relayMetadata = { relays: updated, updatedAt: Date.now() };
    localStorage.setItem('nostr:app-config', JSON.stringify(cfg));
  };

  const addRelay = (url?: string) => {
    const target = (url || newRelay).trim();
    if (!target.startsWith('wss://')) return;
    if (relays.some(r => r.url === target)) return;
    saveRelays([...relays, { url: target, read: true, write: true }]);
    if (!url) setNewRelay('');
  };

  const removeRelay = (url: string) => {
    saveRelays(relays.filter(r => r.url !== url));
  };

  const toggleRead = (url: string) => {
    saveRelays(relays.map(r => r.url === url ? { ...r, read: !r.read } : r));
  };

  const toggleWrite = (url: string) => {
    saveRelays(relays.map(r => r.url === url ? { ...r, write: !r.write } : r));
  };

  const testAll = async () => {
    setTesting(true);
    const allUrls = [
      ...relays.map(r => r.url),
      ...CRITICAL_RELAYS.map(r => r.url).filter(u => !relays.some(r => r.url === u)),
    ];
    // Set all to checking
    setStatuses(Object.fromEntries(allUrls.map(u => [u, 'checking' as RelayStatus])));
    // Test in parallel
    await Promise.all(allUrls.map(async (url) => {
      const status = await testRelayConnection(url);
      setStatuses(prev => ({ ...prev, [url]: status }));
    }));
    setTesting(false);
  };

  // Health checks
  const writeRelays = relays.filter(r => r.write !== false);
  const missingCritical = CRITICAL_RELAYS.filter(
    cr => !relays.some(r => r.url === cr.url && r.write !== false)
  );
  const hasNoRelays = relays.length === 0;
  const hasNoWriteRelays = relays.length > 0 && writeRelays.length === 0;

  const statusDot = (url: string) => {
    const s = statuses[url];
    if (!s || s === 'unknown') return null;
    if (s === 'checking') return <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" title="Checking…" />;
    if (s === 'online') return <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" title="Online" />;
    return <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" title="Offline / unreachable" />;
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-semibold text-sm mb-1">Relay Connections</h3>
        <p className="text-xs text-muted-foreground">
          Relays are how your posts reach other Nostr users. You need at least one <strong>write</strong> relay in common with the people you want to reach.
        </p>
      </div>

      {/* ── Health Warnings ── */}
      {(hasNoRelays || hasNoWriteRelays || missingCritical.length > 0) && (
        <div className="space-y-2.5">
          {hasNoRelays && (
            <div className="flex items-start gap-3 p-3.5 rounded-xl bg-red-500/10 border border-red-500/25">
              <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-red-600 dark:text-red-400">No relays configured</p>
                <p className="text-xs text-muted-foreground mt-0.5">Your posts aren't being broadcast to anyone. Add the recommended relays below.</p>
              </div>
            </div>
          )}
          {hasNoWriteRelays && !hasNoRelays && (
            <div className="flex items-start gap-3 p-3.5 rounded-xl bg-red-500/10 border border-red-500/25">
              <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-red-600 dark:text-red-400">No write relays enabled</p>
                <p className="text-xs text-muted-foreground mt-0.5">All your relays are read-only. Enable write on at least one relay so your posts can be published.</p>
              </div>
            </div>
          )}
          {missingCritical.map(cr => (
            <div key={cr.url} className="flex items-start gap-3 p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/25">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">Missing: {cr.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{cr.reason}</p>
              </div>
              <button
                onClick={() => addRelay(cr.url)}
                className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 transition-colors"
              >
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── All good banner ── */}
      {!hasNoRelays && !hasNoWriteRelays && missingCritical.length === 0 && (
        <div className="flex items-center gap-3 p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/25">
          <Check className="w-4 h-4 text-emerald-500 shrink-0" />
          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
            Your relay setup looks good — all recommended relays are connected with write access.
          </p>
        </div>
      )}

      {/* ── Add relay input ── */}
      <div className="flex gap-2">
        <input
          value={newRelay}
          onChange={e => setNewRelay(e.target.value)}
          placeholder="wss://relay.example.com"
          className="flex-1 bg-muted rounded-xl px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40 placeholder:text-muted-foreground/50"
          onKeyDown={e => { if (e.key === 'Enter') addRelay(); }}
        />
        <button
          onClick={() => addRelay()}
          disabled={!newRelay.startsWith('wss://')}
          className="px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-40 hover:bg-primary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* ── Relay list ── */}
      <div className="space-y-2">
        {relays.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">No relays added yet.</p>
        )}
        {relays.map(relay => (
          <div key={relay.url} className="flex items-center gap-2 p-3 rounded-xl bg-muted/50 border border-border">
            {statusDot(relay.url) ?? <div className="w-2 h-2 rounded-full bg-muted-foreground/30 shrink-0" />}
            <span className="flex-1 text-xs font-mono truncate min-w-0" title={relay.url}>{relay.url}</span>
            {/* R/W toggles */}
            <button
              onClick={() => toggleRead(relay.url)}
              title="Toggle read"
              className={`px-1.5 py-0.5 rounded text-[10px] font-bold transition-colors ${relay.read !== false ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}
            >R</button>
            <button
              onClick={() => toggleWrite(relay.url)}
              title="Toggle write"
              className={`px-1.5 py-0.5 rounded text-[10px] font-bold transition-colors ${relay.write !== false ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-muted text-muted-foreground'}`}
            >W</button>
            <button onClick={() => removeRelay(relay.url)} className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      {/* ── Test connections button ── */}
      {relays.length > 0 && (
        <button
          onClick={testAll}
          disabled={testing}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${testing ? 'animate-spin' : ''}`} />
          {testing ? 'Testing connections…' : 'Test relay connections'}
        </button>
      )}

      {/* ── Recommended relays ── */}
      <div className="space-y-2 pt-2 border-t border-border">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Recommended Relays</p>
        {CRITICAL_RELAYS.map(cr => {
          const already = relays.some(r => r.url === cr.url);
          const hasWrite = relays.some(r => r.url === cr.url && r.write !== false);
          return (
            <div key={cr.url} className="flex items-center gap-2 p-3 rounded-xl bg-muted/30 border border-border">
              {statusDot(cr.url) ?? <div className={`w-2 h-2 rounded-full shrink-0 ${already && hasWrite ? 'bg-emerald-500' : already ? 'bg-amber-400' : 'bg-muted-foreground/30'}`} />}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold">{cr.label}</p>
                <p className="text-[10px] font-mono text-muted-foreground truncate">{cr.url}</p>
              </div>
              {already && hasWrite
                ? <span className="shrink-0 text-[10px] font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><Check className="w-3 h-3" />Active</span>
                : already && !hasWrite
                  ? <button onClick={() => toggleWrite(cr.url)} className="shrink-0 px-2.5 py-1 rounded-lg bg-amber-500 text-white text-[10px] font-bold hover:bg-amber-600 transition-colors">Enable Write</button>
                  : <button onClick={() => addRelay(cr.url)} className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg bg-primary text-primary-foreground text-[10px] font-bold hover:bg-primary/90 transition-colors"><Plus className="w-3 h-3" />Add</button>
              }
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WalletSettings() {
  const { user } = useAppContext();
  const tipWallet = useTipWallet();
  const [copied, setCopied] = useState(false);
  const [showWIF, setShowWIF] = useState(false);
  const [importWif, setImportWif] = useState('');
  const [importing, setImporting] = useState(false);
  const [importMode, setImportMode] = useState<'wif' | 'seed'>('seed');
  const [importError, setImportError] = useState<string | null>(null);
  const initCalledRef = useRef(false);
  // Send BCH
  const [sendAddr, setSendAddr] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [sendMemo, setSendMemo] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  // Deposit mode: plain BCH vs. CashToken-aware (PUSD and other tokens)
  const [depositTab, setDepositTab] = useState<'bch' | 'token'>('bch');
  // Send currency: 'bch' or a held token's ticker symbol (PUSD, FURU, RES, …).
  const [sendCurrency, setSendCurrency] = useState<string>('bch');
  // Resolved curated tip-token allowlist (for displaying held token balances)
  const [tipTokens, setTipTokens] = useState<TipToken[]>(() => getResolvedTipTokens() ?? []);
  useEffect(() => {
    let alive = true;
    resolveTipTokens().then(list => { if (alive) setTipTokens(list); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const { publish } = useNostrPublish();

  // ── Sendable tokens ────────────────────────────────────────────
  // Any allowlisted CashToken the wallet actually holds (resolved category +
  // positive balance) can be sent, not just PUSD. BCH is always first.
  const sendTokenBalances: Record<string, bigint> = tipWallet.tokenBalances ?? {};
  const heldSendTokens = tipTokens.filter(
    t => t.category && (sendTokenBalances[t.category.toLowerCase()] ?? BigInt(0)) > BigInt(0),
  );
  const selectedSendToken = sendCurrency === 'bch'
    ? null
    : heldSendTokens.find(t => t.symbol === sendCurrency) ?? null;
  // Base units needed to fund a token transfer (dust output + miner-fee buffer).
  const TOKEN_SEND_BCH_BUFFER = BigInt(1800);

  // If the selected token is spent down to zero (balance update, send), fall
  // back to BCH so the form never references a token the wallet no longer holds.
  useEffect(() => {
    if (sendCurrency !== 'bch' && !heldSendTokens.some(t => t.symbol === sendCurrency)) {
      setSendCurrency('bch');
    }
  }, [sendCurrency, heldSendTokens]);

  // Init wallet once on mount — use a ref to prevent StrictMode double-invocation issues
  useEffect(() => {
    if (initCalledRef.current) return;
    initCalledRef.current = true;
    tipWallet.init().catch(() => {});
  }, []);

  const bchPrice = getBCHPrice();
  const walletBalanceUSD = bchPrice ? Number(tipWallet.balanceSats) / 1e8 * bchPrice : 0;
  const wif = tipWallet.getWIF();

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSend = async () => {
    if (!sendAddr.trim() || !sendAmount.trim()) { setSendError('Enter address and amount'); return; }
    if (!tipWallet.ready) { setSendError('Wallet not ready'); return; }

    // ── CashToken send (PUSD, MUSD, RES, LIFT, FURU, …) ─────────
    if (selectedSendToken && selectedSendToken.category) {
      const token = selectedSendToken;
      const category = selectedSendToken.category;
      const amountBase = wholeToBaseUnits(parseFloat(sendAmount), token);
      if (amountBase <= BigInt(0)) { setSendError(`Invalid ${token.symbol} amount`); return; }
      const held = sendTokenBalances[category.toLowerCase()] ?? BigInt(0);
      if (held < amountBase) { setSendError(`Insufficient ${token.symbol} balance`); return; }
      if (tipWallet.balanceSats < TOKEN_SEND_BCH_BUFFER) {
        setSendError('Need a little BCH to cover the token network fee');
        return;
      }
      const fullAddrT = normalizeBCHAddress(sendAddr);
      if (!fullAddrT) { setSendError('Invalid address — use a CashAddr (starts with q, p, z or r)'); return; }

      setSending(true); setSendError(null); setSendResult(null);
      try {
        const txid = await tipWallet.sendToken(fullAddrT, category, amountBase);
        setSendResult(txid || 'sent');
        if (sendMemo.trim()) {
          const whole = baseUnitsToWhole(amountBase, token.decimals);
          const amountLabel = token.stable
            ? `$${whole.toFixed(2)} ${token.symbol}`
            : `${whole.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${token.symbol}`;
          try {
            await publish({
              kind: 1,
              content: `\u{1F4B5} Sent ${amountLabel}${sendMemo.trim() ? ': ' + sendMemo.trim() : ''}\n\n${txid ? 'TX: ' + txid : ''}\n\n#${token.symbol.toLowerCase()} #bch #bitcoincash`,
              tags: [
                ['t', token.symbol.toLowerCase()], ['t', 'bch'], ['t', 'bitcoincash'],
                ...(txid ? [['txid', txid]] : []),
              ],
              created_at: Math.floor(Date.now() / 1000),
            });
          } catch {}
        }
        setSendAddr(''); setSendAmount(''); setSendMemo('');
        setTimeout(() => { tipWallet.refreshTokenBalances(); tipWallet.refreshBalance(); }, 2000);
      } catch (e) {
        setSendError(e instanceof Error ? e.message : `${token.symbol} send failed`);
      } finally { setSending(false); }
      return;
    }

    const sats = Math.round(parseFloat(sendAmount) * 1e8);
    if (isNaN(sats) || sats <= 0) { setSendError('Invalid amount'); return; }
    if (tipWallet.balanceSats < BigInt(sats) + BigInt(300)) {
      setSendError(`Insufficient balance. Need ${(sats / 1e8).toFixed(8)} + fees.`);
      return;
    }
    // Validate address using shared CashAddr validator
    const fullAddr = normalizeBCHAddress(sendAddr);
    if (!fullAddr) { setSendError('Invalid BCH address — use CashAddr format (starts with q or p)'); return; }

    setSending(true);
    setSendError(null);
    setSendResult(null);
    try {
      const txid = await tipWallet.sendBCH(fullAddr, BigInt(sats));
      setSendResult(txid || 'sent');

      // Publish memo as a Nostr note if provided
      if (sendMemo.trim()) {
        try {
          await publish({
            kind: 1,
            content: `\u{1F4B8} Sent ${(sats / 1e8).toFixed(8)} BCH${sendMemo.trim() ? ': ' + sendMemo.trim() : ''}\n\n${txid ? 'TX: ' + txid : ''}\n\n#bch #bitcoincash`,
            tags: [
              ['t', 'bch'], ['t', 'bitcoincash'],
              ...(txid ? [['txid', txid]] : []),
            ],
            created_at: Math.floor(Date.now() / 1000),
          });
        } catch {} // Non-critical
      }

      setSendAddr('');
      setSendAmount('');
      setSendMemo('');
      setTimeout(() => tipWallet.refreshBalance(), 2000);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : 'Send failed');
    } finally { setSending(false); }
  };

  const handleImport = async () => {
    if (!importWif.trim()) return;
    setImporting(true);
    setImportError(null);
    try {
      if (importMode === 'seed') {
        await tipWallet.importMnemonic(importWif.trim());
      } else {
        await tipWallet.importWIF(importWif.trim());
      }
      setImportWif('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      setImportError(msg);
      console.error('Import failed:', err);
    } finally {
      setImporting(false);
    }
  };

   const hasBalance = Number(tipWallet.balanceSats) > 0;

  return (
    <div className="space-y-4">
      {/* Backup Warning Banner */}
      {hasBalance && tipWallet.ready && (
        <div className="flex items-start gap-3 rounded-xl bg-amber-500/10 border border-amber-500/30 p-4">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-700 dark:text-amber-400">Back up your wallet key!</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Your BCH tip wallet is stored in this browser only. If you clear your browser data, switch devices, or reinstall your browser, <strong className="text-foreground">your funds will be lost</strong>. Scroll down to <strong className="text-foreground">Wallet Key (WIF)</strong> and save it somewhere safe — a password manager, written down, or in another BCH wallet like Electron Cash.
            </p>
          </div>
        </div>
      )}

      {/* Balance Card */}
      <div className="glass-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <Wallet className="w-4 h-4 text-primary" /> BCH Tip Wallet
          </h3>
          <button
            onClick={() => { tipWallet.refreshBalance(); tipWallet.refreshPusdBalance(); }}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted"
          >
            <RefreshCw className={`w-4 h-4 ${tipWallet.loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {tipWallet.loading ? (
          <div className="flex flex-col items-center justify-center py-6 gap-2">
            <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            <p className="text-xs text-muted-foreground">Setting up wallet…</p>
          </div>
        ) : tipWallet.ready ? (
          <div className="space-y-4">
            {/* Balance Display */}
            <div className="text-center py-3">
              <p className="text-3xl font-bold font-mono">
                {(Number(tipWallet.balanceSats) / 1e8).toFixed(8).replace(/0+$/, '').replace(/\.$/, '') || '0'}
              </p>
              <p className="text-sm text-muted-foreground">BCH</p>
              {walletBalanceUSD > 0 && (
                <p className="text-xs text-muted-foreground mt-0.5">${walletBalanceUSD.toFixed(2)} USD</p>
              )}
              {(() => {
                const balances: Record<string, bigint> = tipWallet.tokenBalances ?? {};
                const held = tipTokens.filter(t => t.category && (balances[t.category.toLowerCase()] ?? BigInt(0)) > BigInt(0));
                if (held.length === 0) return null;
                return (
                  <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
                    {held.map(t => {
                      const base = balances[t.category!.toLowerCase()] ?? BigInt(0);
                      const usd = tokenBalanceUsd(base, t);
                      const whole = baseUnitsToWhole(base, t.decimals);
                      return (
                        <div key={t.symbol} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1">
                          {t.iconUrl
                            ? <img src={t.iconUrl} alt="" className="w-3 h-3 rounded-full" loading="lazy" />
                            : <Coins className="w-3 h-3 text-emerald-500" />}
                          <span className="text-xs font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                            {usd != null && t.stable
                              ? `$${usd.toFixed(2)} ${t.symbol}`
                              : `${whole.toLocaleString(undefined, { maximumFractionDigits: t.decimals === 0 ? 0 : 4 })} ${t.symbol}`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Send */}
            {tipWallet.ready && (
              <div className="space-y-3 pt-3 border-t border-border">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <ArrowUpRight className="w-3.5 h-3.5" /> Send {selectedSendToken ? selectedSendToken.symbol : 'BCH'}
                </p>

                {/* Currency selector — BCH plus every CashToken the wallet
                    actually holds (PUSD, FURU, RES, LIFT, MUSD, …). */}
                {heldSendTokens.length > 0 && (
                  <div className="flex flex-wrap gap-1 p-1 bg-muted rounded-lg">
                    <button
                      onClick={() => { setSendCurrency('bch'); setSendAmount(''); setSendError(null); }}
                      className={`flex-1 min-w-[56px] py-1.5 rounded-md text-xs font-medium transition-colors ${sendCurrency === 'bch' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      BCH
                    </button>
                    {heldSendTokens.map(t => (
                      <button
                        key={t.symbol}
                        onClick={() => { setSendCurrency(t.symbol); setSendAmount(''); setSendError(null); }}
                        className={`flex-1 min-w-[56px] flex items-center justify-center gap-1 py-1.5 rounded-md text-xs font-medium transition-colors ${sendCurrency === t.symbol ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        {t.iconUrl && <img src={t.iconUrl} alt="" className="w-3 h-3 rounded-full" loading="lazy" />}
                        {t.symbol}
                      </button>
                    ))}
                  </div>
                )}

                <div>
                  <label className="text-[10px] text-muted-foreground">Recipient Address</label>
                  <input
                    value={sendAddr}
                    onChange={e => setSendAddr(e.target.value)}
                    placeholder="bitcoincash:q... or q..."
                    className="w-full bg-muted rounded-lg px-3 py-2 text-xs font-mono mt-0.5 focus:outline-none focus:ring-1 focus:ring-primary"
                    spellCheck={false}
                  />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">
                    Amount ({selectedSendToken ? selectedSendToken.symbol : 'BCH'})
                  </label>
                  <div className="flex gap-2 mt-0.5">
                    <input
                      type="number"
                      value={sendAmount}
                      onChange={e => setSendAmount(e.target.value)}
                      placeholder={selectedSendToken ? (selectedSendToken.stable ? '1.00' : '0') : '0.00010000'}
                      step={selectedSendToken ? (selectedSendToken.decimals === 0 ? '1' : '0.01') : '0.00000001'}
                      className="flex-1 bg-muted rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <button
                      onClick={() => {
                        if (selectedSendToken && selectedSendToken.category) {
                          const bal = sendTokenBalances[selectedSendToken.category.toLowerCase()] ?? BigInt(0);
                          const whole = baseUnitsToWhole(bal, selectedSendToken.decimals);
                          setSendAmount(selectedSendToken.decimals === 0
                            ? String(Math.floor(whole))
                            : whole.toFixed(Math.min(selectedSendToken.decimals, selectedSendToken.stable ? 2 : 4)));
                        } else {
                          // Max send: balance minus fee buffer
                          const max = Number(tipWallet.balanceSats) - 600;
                          if (max > 0) setSendAmount((max / 1e8).toFixed(8));
                        }
                      }}
                      className="px-2 py-2 rounded-lg border border-border text-[10px] text-muted-foreground hover:text-foreground shrink-0"
                    >
                      Max
                    </button>
                  </div>
                  {sendCurrency === 'bch' && sendAmount && parseFloat(sendAmount) > 0 && bchPrice && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">≈ ${(parseFloat(sendAmount) * bchPrice).toFixed(2)} USD</p>
                  )}
                  {selectedSendToken && (
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {selectedSendToken.stable
                        ? `${selectedSendToken.symbol} is a ~$1 stablecoin · network fee paid in BCH`
                        : `Amount in whole ${selectedSendToken.symbol} · network fee paid in BCH`}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground">Message (optional)</label>
                  <input
                    value={sendMemo}
                    onChange={e => setSendMemo(e.target.value)}
                    placeholder="e.g. Faucet payout, thanks for tipping!"
                    className="w-full bg-muted rounded-lg px-3 py-2 text-xs mt-0.5 focus:outline-none focus:ring-1 focus:ring-primary"
                    maxLength={200}
                  />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Published as a Nostr note with the transaction</p>
                </div>
                <button
                  onClick={handleSend}
                  disabled={sending || !sendAddr.trim() || !sendAmount.trim()}
                  className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sending ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
                  {sending ? 'Sending...' : `Send ${selectedSendToken ? selectedSendToken.symbol : 'BCH'}`}
                </button>
                {sendResult && (
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-2.5 space-y-1">
                    <p className="text-xs font-medium text-emerald-500 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Sent!</p>
                    {sendResult !== 'sent' && (
                      <a href={`https://bchexplorer.cash/tx/${sendResult}`} target="_blank" rel="noopener" className="text-[10px] text-primary hover:underline font-mono">{sendResult.slice(0, 16)}...</a>
                    )}
                  </div>
                )}
                {sendError && (
                  <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-2">
                    <p className="text-[10px] text-red-500">{sendError}</p>
                  </div>
                )}
              </div>
            )}

            {/* Deposit Address */}
            {tipWallet.fullAddress && (() => {
              // Which address to display depends on the selected tab. The
              // token-aware address is the same payload with a token-type
              // CashAddr prefix — token-aware wallets must send CashTokens
              // (e.g. PUSD) to THIS address so the receiver can handle them.
              const isToken = depositTab === 'token';
              const displayFull = isToken ? (tipWallet.fullTokenAddress || tipWallet.fullAddress) : tipWallet.fullAddress!;
              const displayNaked = isToken ? (tipWallet.tokenAddress || tipWallet.address) : tipWallet.address;
              // Only show the token tab if we actually derived a distinct
              // token address — otherwise mainnet-js couldn't produce one.
              const hasToken = !!tipWallet.fullTokenAddress && tipWallet.fullTokenAddress !== tipWallet.fullAddress;

              return (
                <div className="space-y-3">
                  <p className="text-xs font-medium text-muted-foreground">Deposit Address:</p>

                  {/* BCH vs. Token (PUSD) tabs */}
                  {hasToken && (
                    <div className="flex gap-1 p-1 bg-muted rounded-lg">
                      <button
                        onClick={() => setDepositTab('bch')}
                        className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${!isToken ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        BCH
                      </button>
                      <button
                        onClick={() => setDepositTab('token')}
                        className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${isToken ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      >
                        Tokens (PUSD)
                      </button>
                    </div>
                  )}

                  {isToken && hasToken && (
                    <div className="flex items-start gap-2 rounded-lg bg-primary/10 border border-primary/20 p-2.5">
                      <Info className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                      <p className="text-[10px] text-muted-foreground leading-relaxed">
                        This is a <strong className="text-foreground">token-aware address</strong>. Use it to receive CashTokens like <strong className="text-foreground">PUSD</strong>. Only send tokens from a CashToken-capable wallet. Plain BCH can still be sent here too.
                      </p>
                    </div>
                  )}

                  <div className="p-3 bg-white rounded-xl border shadow-sm mx-auto w-fit">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(displayFull)}&ecc=H&margin=4`}
                      alt={isToken ? 'Token deposit QR Code' : 'Deposit QR Code'}
                      className="w-44 h-44 rounded-lg"
                      loading="eager"
                    />
                  </div>
                  <div className="flex items-center gap-1 bg-muted rounded-lg p-2">
                    <code className="flex-1 text-[10px] font-mono text-muted-foreground truncate">{displayNaked}</code>
                    <button
                      onClick={() => handleCopy(displayFull)}
                      className="shrink-0 p-1.5 rounded hover:bg-background"
                    >
                      {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-muted-foreground" />}
                    </button>
                  </div>
                  <a
                    href={`https://bchexplorer.cash/address/${tipWallet.address}`}
                    target="_blank"
                    rel="noopener"
                    className="flex items-center justify-center gap-1.5 text-xs text-primary hover:underline"
                  >
                     <ArrowUpRight className="w-3 h-3" /> View on Explorer
                  </a>
                </div>
              );
            })()}

            {tipWallet.error && (
              <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3">
                <p className="text-xs text-red-500 break-all">{tipWallet.error}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4 space-y-3">
            {tipWallet.error ? (
              <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 text-left">
                <p className="text-xs text-red-500 break-all">{tipWallet.error}</p>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Wallet not initialized. Click below to set up your BCH tip wallet.
              </p>
            )}
            <button
              onClick={() => { initCalledRef.current = false; tipWallet.init().catch(() => {}); }}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
            >
              {tipWallet.error ? 'Retry' : 'Initialize Wallet'}
            </button>
          </div>
        )}
      </div>

      {/* Wallet Recovery / WIF */}
      <div className="glass-card">
        <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
          <Key className="w-4 h-4 text-primary" /> Wallet Key (WIF)
        </h3>
        <div className="flex items-start gap-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">
            Your WIF private key controls your BCH funds. <strong>Never share it.</strong> Back it up safely — you can import it into Electron Cash or any BCH wallet.
          </p>
        </div>

        {tipWallet.hasWallet && wif && (
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-muted rounded-lg p-2">
                <code className="text-[10px] font-mono break-all">
                  {showWIF ? wif : '\u{2022}'.repeat(52)}
                </code>
              </div>
              <button
                onClick={() => setShowWIF(!showWIF)}
                className="p-2 rounded-lg hover:bg-muted text-muted-foreground"
                title={showWIF ? 'Hide' : 'Reveal'}
              >
                {showWIF ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {showWIF && (
              <button
                onClick={() => handleCopy(wif)}
                className="flex items-center gap-1.5 text-xs text-primary hover:underline"
              >
                <Copy className="w-3 h-3" /> {copied ? 'Copied!' : 'Copy WIF'}
              </button>
            )}
          </div>
        )}

        {/* If the wallet is PIN-locked, surface the unlock prompt right here so
            the user can regain access where they spend. Full PIN management
            (enable / change / remove) lives in the Security tab. */}
        <WalletEncryptionSection tipWallet={tipWallet} variant="unlock-only" />

        {/* Manage wallet-key encryption in the Security tab */}
        <Link
          to="/settings?tab=security"
          className="mt-4 flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 hover:bg-muted/50 transition-colors"
        >
          <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
          <span className="text-sm font-medium flex-1">
            {tipWallet.encrypted ? 'Wallet key is PIN-protected — manage' : 'Protect your wallet key with a PIN'}
          </span>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
        </Link>

        {/* Import Wallet */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Upload className="w-3 h-3" /> Import existing wallet
          </p>
          {/* Mode tabs */}
          <div className="flex gap-1 bg-muted rounded-lg p-0.5">
            <button
              onClick={() => { setImportMode('seed'); setImportWif(''); setImportError(null); }}
              className={`flex-1 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
                importMode === 'seed' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Seed Phrase (12/24 words)
            </button>
            <button
              onClick={() => { setImportMode('wif'); setImportWif(''); setImportError(null); }}
              className={`flex-1 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
                importMode === 'wif' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              WIF Private Key
            </button>
          </div>
          {/* Input */}
          <div className="flex gap-2">
            {importMode === 'seed' ? (
              <textarea
                value={importWif}
                onChange={e => { setImportWif(e.target.value); setImportError(null); }}
                placeholder="Enter your 12 or 24 word seed phrase..."
                className="flex-1 bg-muted rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                rows={2}
                spellCheck={false}
                autoComplete="off"
              />
            ) : (
              <input
                type="text"
                value={importWif}
                onChange={e => { setImportWif(e.target.value); setImportError(null); }}
                placeholder="Paste WIF private key (starts with K, L, or 5)..."
                className="flex-1 bg-muted rounded-lg px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleImport}
              disabled={importing || !importWif.trim()}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50"
            >
              {importing ? 'Importing...' : 'Import Wallet'}
            </button>
            {importMode === 'seed' && importWif.trim() && (
              <span className="text-[10px] text-muted-foreground">
                {importWif.trim().split(/\s+/).length} words
              </span>
            )}
          </div>
          {importError && (
            <p className="text-[11px] text-red-500">{importError}</p>
          )}
          <p className="text-[10px] text-muted-foreground">
            {importMode === 'seed'
              ? 'Standard BIP44 derivation path (m/44\'/145\'/0\'/0/0) is used. Compatible with Electron Cash, Bitcoin.com, and most BCH wallets.'
              : 'Paste a WIF private key starting with K, L, or 5.'}
          </p>

          {/* Reusing one seed across identities defeats the per-identity
              wallet separation — surface the trade-off before they do it. */}
          <div className="flex items-start gap-1.5 rounded-lg bg-amber-500/[0.08] border border-amber-500/25 px-2.5 py-2">
            <Info className="w-3 h-3 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              {user?.isBusiness
                ? 'You can import the same seed your personal account uses, but then both share one address and balance — business and personal tips get mixed together. Keeping this Business Page on its own wallet is what keeps the funds (and the on-chain trail) separate.'
                : 'Each account — personal or Business Page — starts with its own wallet. You can reuse a seed across them, but then they share one address and balance, which undoes that separation. Only do it if you deliberately want both to spend from the same pot.'}
            </p>
          </div>
        </div>

        {/* Generate new wallet */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            onClick={() => {
              if (tipWallet.hasWallet) {
                if (!confirm('This will replace your current wallet! Make sure you have backed up your WIF key first.')) return;
              }
              tipWallet.generateNewWallet();
            }}
            disabled={tipWallet.loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary text-primary text-xs font-medium hover:bg-primary/5 disabled:opacity-50"
          >
            <Plus className="w-3 h-3" /> Generate New Wallet
          </button>

          {/* Delete wallet */}
          {tipWallet.hasWallet && (
            <button
              onClick={() => {
                if (confirm('Delete wallet from this device AND Nostr? Make sure you have backed up your WIF key!')) {
                  tipWallet.clearWallet();
                }
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-500/30 text-red-500 text-xs font-medium hover:bg-red-500/5"
            >
              <Trash2 className="w-3 h-3" /> Delete Wallet
            </button>
          )}
        </div>
      </div>

      {/* Transaction History */}
      <TransactionHistory />

      {/* WizardConnect */}
      <WizardConnectPanel />
    </div>
  );
}

// ── Transaction History (embedded in Wallet) ─────────────────────

interface ParsedTx {
  id: string;
  event: import('nostr-tools').Event;
  direction: 'sent' | 'received';
  counterpartyPubkey: string;
  amountSats: number;
  amountUsd: number;
  txid: string;
  eventId?: string;
  timestamp: number;
}

function parseTipReceipt(ev: import('nostr-tools').Event, myPubkey: string): ParsedTx | null {
  const pTag = ev.tags.find(t => t[0] === 'p')?.[1];
  if (!pTag) return null;

  const isSender = ev.pubkey === myPubkey;
  const isRecipient = pTag === myPubkey;
  if (!isSender && !isRecipient) return null;

  let amountSats = 0;
  let amountUsd = 0;
  let txid = '';

  try {
    const json = JSON.parse(ev.content);
    amountSats = json.amount_sats || 0;
    amountUsd = json.amount_usd || 0;
    txid = json.txid || '';
  } catch {
    const amountTag = ev.tags.find(t => t[0] === 'amount')?.[1];
    if (amountTag) amountSats = parseInt(amountTag) || 0;
  }

  if (amountSats === 0) {
    const amountTag = ev.tags.find(t => t[0] === 'amount')?.[1];
    if (amountTag) {
      const msats = parseInt(amountTag) || 0;
      amountSats = msats > 100_000 ? Math.round(msats / 1000) : msats;
    }
  }

  const eventId = ev.tags.find(t => t[0] === 'e')?.[1];

  return {
    id: ev.id,
    event: ev,
    direction: isSender ? 'sent' : 'received',
    counterpartyPubkey: isSender ? pTag : ev.pubkey,
    amountSats,
    amountUsd,
    txid,
    eventId,
    timestamp: ev.created_at,
  };
}

function formatTxTime(ts: number): string {
  const secs = Math.floor(Date.now() / 1000 - ts);
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  if (secs < 604800) return `${Math.floor(secs / 86400)}d ago`;
  return new Date(ts * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatFullDate(ts: number): string {
  return new Date(ts * 1000).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatBCH(sats: number): string {
  const bch = sats / 1e8;
  if (bch === 0) return '0';
  if (bch < 0.001) return `${sats.toLocaleString()} sats`;
  return bch.toFixed(bch < 0.01 ? 6 : 4) + ' BCH';
}

type TxDirFilter = 'all' | 'sent' | 'received';

function TransactionHistory() {
  const { user } = useAppContext();
  const [dirFilter, setDirFilter] = useState<TxDirFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const bchPrice = getBCHPrice();

  const { events: sentEvents, loading: sentLoading } = useNostrQuery({
    filters: user?.pubkey ? [
      { kinds: [9735], authors: [user.pubkey], limit: 500 },
    ] : [],
    enabled: !!user?.pubkey,
  });

  const { events: recvEvents, loading: recvLoading } = useNostrQuery({
    filters: user?.pubkey ? [
      { kinds: [9735], '#p': [user.pubkey], limit: 500 },
    ] : [],
    enabled: !!user?.pubkey,
  });

  const loading = sentLoading || recvLoading;

  const transactions = React.useMemo(() => {
    if (!user?.pubkey) return [];
    const seen = new Set<string>();
    const txs: ParsedTx[] = [];
    for (const ev of [...sentEvents, ...recvEvents]) {
      if (seen.has(ev.id)) continue;
      seen.add(ev.id);
      const tx = parseTipReceipt(ev, user.pubkey);
      if (tx && tx.amountSats > 0) txs.push(tx);
    }
    txs.sort((a, b) => b.timestamp - a.timestamp);
    return txs;
  }, [sentEvents, recvEvents, user?.pubkey]);

  const filtered = React.useMemo(() => {
    let list = transactions;
    if (dirFilter !== 'all') {
      list = list.filter(tx => tx.direction === dirFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(tx =>
        tx.counterpartyPubkey.toLowerCase().includes(q) ||
        tx.txid.toLowerCase().includes(q) ||
        (tx.eventId || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [transactions, dirFilter, searchQuery]);

  const stats = React.useMemo(() => {
    const sent = transactions.filter(tx => tx.direction === 'sent');
    const recv = transactions.filter(tx => tx.direction === 'received');
    return {
      totalSent: sent.reduce((sum, tx) => sum + tx.amountSats, 0),
      totalReceived: recv.reduce((sum, tx) => sum + tx.amountSats, 0),
      sentCount: sent.length,
      recvCount: recv.length,
    };
  }, [transactions]);

  const INITIAL_SHOW = 10;
  const displayed = showAll ? filtered : filtered.slice(0, INITIAL_SHOW);

  return (
    <div className="glass-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Coins className="w-4 h-4 text-primary" /> Transaction History
        </h3>
        {loading && <Loader2 className="w-3.5 h-3.5 text-muted-foreground animate-spin" />}
      </div>

      {/* Summary */}
      {transactions.length > 0 && (
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-2.5">
            <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 mb-0.5">
              <ArrowDownLeft className="w-3 h-3" />
              <span className="text-[10px] font-semibold uppercase tracking-wider">Received</span>
            </div>
            <p className="text-xs font-bold font-mono">{formatBCH(stats.totalReceived)}</p>
            {bchPrice && stats.totalReceived > 0 && (
              <p className="text-[10px] text-muted-foreground">≈ ${(stats.totalReceived / 1e8 * bchPrice).toFixed(2)}</p>
            )}
            <p className="text-[10px] text-muted-foreground">{stats.recvCount} tip{stats.recvCount !== 1 ? 's' : ''}</p>
          </div>
          <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-2.5">
            <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 mb-0.5">
              <ArrowUpRight className="w-3 h-3" />
              <span className="text-[10px] font-semibold uppercase tracking-wider">Sent</span>
            </div>
            <p className="text-xs font-bold font-mono">{formatBCH(stats.totalSent)}</p>
            {bchPrice && stats.totalSent > 0 && (
              <p className="text-[10px] text-muted-foreground">≈ ${(stats.totalSent / 1e8 * bchPrice).toFixed(2)}</p>
            )}
            <p className="text-[10px] text-muted-foreground">{stats.sentCount} tip{stats.sentCount !== 1 ? 's' : ''}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      {transactions.length > 0 && (
        <div className="flex items-center gap-2 mb-3">
          <div className="flex gap-0.5 bg-muted/60 rounded-lg p-0.5">
            {(['all', 'received', 'sent'] as TxDirFilter[]).map(dir => (
              <button
                key={dir}
                onClick={() => setDirFilter(dir)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  dirFilter === dir
                    ? dir === 'received'
                      ? 'bg-emerald-500 text-white'
                      : dir === 'sent'
                        ? 'bg-blue-500 text-white'
                        : 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {dir === 'all' ? 'All' : dir === 'received' ? '↓ Recv' : '↑ Sent'}
              </button>
            ))}
          </div>
          <div className="flex-1 relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search pubkey or txid…"
              className="w-full bg-muted/60 rounded-lg pl-7 pr-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      )}

      {/* Transaction list */}
      {transactions.length === 0 && !loading ? (
        <div className="text-center py-8">
          <Coins className="w-8 h-8 mx-auto text-muted-foreground/20 mb-2" />
          <p className="text-xs text-muted-foreground">No transactions yet.</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">Tips you send and receive will appear here.</p>
        </div>
      ) : filtered.length === 0 && !loading ? (
        <div className="text-center py-6">
          <Filter className="w-6 h-6 mx-auto text-muted-foreground/20 mb-2" />
          <p className="text-xs text-muted-foreground">No matching transactions.</p>
        </div>
      ) : (
        <div className="space-y-0 -mx-4">
          {displayed.map(tx => (
            <TxRow key={tx.id} tx={tx} bchPrice={bchPrice} />
          ))}
        </div>
      )}

      {/* Show more */}
      {!showAll && filtered.length > INITIAL_SHOW && (
        <button
          onClick={() => setShowAll(true)}
          className="w-full mt-2 py-2 text-xs text-primary font-medium hover:bg-primary/5 rounded-lg transition-colors"
        >
          Show all {filtered.length} transactions
        </button>
      )}

      {/* Loading skeleton */}
      {loading && transactions.length === 0 && (
        <div className="space-y-2 -mx-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
              <div className="w-8 h-8 rounded-full bg-muted" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-28 bg-muted rounded" />
                <div className="h-2 w-20 bg-muted rounded" />
              </div>
              <div className="h-3 w-16 bg-muted rounded" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TxRow({ tx, bchPrice }: { tx: ParsedTx; bchPrice: number | null }) {
  const { profile } = useNostrProfile(tx.counterpartyPubkey);
  const name = profile?.display_name || profile?.name || tx.counterpartyPubkey.slice(0, 10) + '…';
  const picture = profile?.picture;
  const isSent = tx.direction === 'sent';

  const usdStr = tx.amountUsd > 0
    ? `$${tx.amountUsd.toFixed(2)}`
    : bchPrice
      ? `≈ $${(tx.amountSats / 1e8 * bchPrice).toFixed(2)}`
      : null;

  return (
    <div className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-muted/30 transition-colors">
      {/* Avatar + direction badge */}
      <div className="relative shrink-0">
        <Link to={`/profile/${tx.counterpartyPubkey}`}>
          {picture ? (
            <img src={picture} alt={name} className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
              <User className="w-4 h-4 text-muted-foreground/40" />
            </div>
          )}
        </Link>
        <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center border-[1.5px] border-background ${
          isSent ? 'bg-blue-500' : 'bg-emerald-500'
        }`}>
          {isSent ? <ArrowUpRight className="w-2.5 h-2.5 text-white" /> : <ArrowDownLeft className="w-2.5 h-2.5 text-white" />}
        </div>
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <Link
          to={`/profile/${tx.counterpartyPubkey}`}
          className="text-xs font-semibold truncate block hover:text-primary transition-colors"
        >
          {name}
        </Link>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`text-[10px] font-medium ${isSent ? 'text-blue-500' : 'text-emerald-500'}`}>
            {isSent ? 'Sent' : 'Received'}
          </span>
          <span className="text-[10px] text-muted-foreground" title={formatFullDate(tx.timestamp)}>
            {formatTxTime(tx.timestamp)}
          </span>
          {tx.eventId && (
            <Link to={`/note/${tx.eventId}`} className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-0.5" title="View tipped post">
              <FileText className="w-2.5 h-2.5" />
            </Link>
          )}
          {tx.txid && (
            <a href={`https://bchexplorer.cash/tx/${tx.txid}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-muted-foreground hover:text-primary flex items-center gap-0.5" title="View on blockchain">
              <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
        </div>
      </div>

      {/* Amount */}
      <div className="text-right shrink-0">
        <p className={`text-xs font-bold font-mono ${isSent ? 'text-blue-500' : 'text-emerald-500'}`}>
          {isSent ? '-' : '+'}{formatBCH(tx.amountSats)}
        </p>
        {usdStr && <p className="text-[10px] text-muted-foreground font-mono">{usdStr}</p>}
      </div>
    </div>
  );
}

function MutedUsersSettings() {
  const { mutedPubkeys, unmute } = useAppContext();
  const mutedList = [...mutedPubkeys];

  if (mutedList.length === 0) {
    return (
      <div className="text-center py-12">
        <Volume2 className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
        <p className="text-sm text-muted-foreground">No muted users.</p>
        <p className="text-xs text-muted-foreground mt-1">When you mute someone, they'll appear here so you can unmute them later.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground mb-3">
        Muted users are hidden from your feed. Unmute them here to see their posts again.
      </p>
      {mutedList.map(pubkey => (
        <MutedUserRow key={pubkey} pubkey={pubkey} onUnmute={() => unmute(pubkey)} />
      ))}
    </div>
  );
}

function MutedUserRow({ pubkey, onUnmute }: { pubkey: string; onUnmute: () => void }) {
  const { profile } = useNostrProfile(pubkey);
  const displayName = profile?.display_name || profile?.name || pubkey.slice(0, 12) + '...';
  const picture = profile?.picture;

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
      {picture ? (
        <img src={picture} alt="" className="w-10 h-10 rounded-full object-cover" />
      ) : (
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <User className="w-5 h-5 text-primary/60" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate">{displayName}</p>
        <p className="text-[10px] text-muted-foreground font-mono truncate">{pubkey.slice(0, 20)}...</p>
      </div>
      <button
        onClick={onUnmute}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-emerald-500/10 hover:border-emerald-500/30 hover:text-emerald-600 transition-colors"
      >
        <Volume2 className="w-3.5 h-3.5" /> Unmute
      </button>
    </div>
  );
}

function BannedUsersSettings() {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [bannedList, setBannedList] = useState(getBannedPubkeys);

  // Re-read when ban list changes
  useEffect(() => {
    return onBanListChange(() => setBannedList(getBannedPubkeys()));
  }, []);

  // Only visible to operator
  if (!user || user.pubkey !== OPERATOR_PUBKEY) return null;

  const handleUnban = async (pubkey: string) => {
    try {
      await publish({
        kind: FLAG_KIND,
        content: 'Unbanned by operator',
        tags: [
          ['t', UNBAN_TAG],
          ['p', pubkey],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      removeLocalBan(pubkey);
    } catch {}
  };

  return (
    <div className="mt-8 pt-6 border-t border-border">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="w-4 h-4 text-red-500" />
        <h3 className="text-sm font-bold text-red-500">Banned Users (Admin)</h3>
        <span className="text-[9px] bg-red-500/15 text-red-500 px-1.5 py-0.5 rounded-full font-semibold">OPERATOR</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Banned users' content is hidden site-wide for all visitors. Ban events are published to Nostr relays.
      </p>

      {bannedList.length === 0 ? (
        <div className="text-center py-8">
          <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground/20 mb-2" />
          <p className="text-sm text-muted-foreground">No banned users.</p>
          <p className="text-xs text-muted-foreground mt-1">To ban a user, visit their profile and click the shield icon.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {bannedList.map(pubkey => (
            <BannedUserRow key={pubkey} pubkey={pubkey} onUnban={() => handleUnban(pubkey)} />
          ))}
        </div>
      )}
    </div>
  );
}

function BannedUserRow({ pubkey, onUnban }: { pubkey: string; onUnban: () => void }) {
  const { profile } = useNostrProfile(pubkey);
  const displayName = profile?.display_name || profile?.name || pubkey.slice(0, 12) + '...';
  const picture = profile?.picture;
  const [unbanning, setUnbanning] = useState(false);

  const handleUnban = async () => {
    setUnbanning(true);
    await onUnban();
    setUnbanning(false);
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
      <Link to={`/profile/${pubkey}`}>
        {picture ? (
          <img src={picture} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
            <User className="w-5 h-5 text-red-500/60" />
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/profile/${pubkey}`} className="text-sm font-semibold truncate block hover:text-primary">{displayName}</Link>
        <p className="text-[10px] text-muted-foreground font-mono truncate">{pubkey.slice(0, 20)}...</p>
      </div>
      <button
        onClick={handleUnban}
        disabled={unbanning}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/30 text-xs font-medium text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50"
      >
        {unbanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />}
        Unban
      </button>
    </div>
  );
}

// ── Manual NIP-05 Verification (Operator Only) ──────────────────
const NIP05_OVERRIDE_KIND = 30078;
const NIP05_OVERRIDE_D_TAG_PREFIX = 'bchnostr/nip05-override/';
const NIP05_OVERRIDE_DOMAIN = 'bchnostr.com';

// ── Admin tab (Operator Only) ───────────────────────────────────
//
// Groups every operator-only tool in one place. If the logged-in key
// isn't the operator we say so explicitly, showing both keys — silently
// rendering nothing is what made these tools look "missing after deploy".

function AdminTab({ userPubkey }: { userPubkey: string }) {
  if (!isOperatorPubkey(userPubkey)) {
    const npub = (() => {
      try { return nip19.npubEncode(userPubkey); } catch { return userPubkey; }
    })();
    const operatorNpub = (() => {
      try { return nip19.npubEncode(OPERATOR_PUBKEY); } catch { return OPERATOR_PUBKEY; }
    })();

    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
        <div className="flex items-center gap-2 mb-2">
          <ShieldAlert className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm font-bold">Operator tools unavailable</h3>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          These tools are restricted to the platform operator account. You're
          logged in with a different key.
        </p>
        <div className="space-y-2">
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Logged in as</p>
            <p className="text-[10px] font-mono break-all">{npub}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Operator account</p>
            <p className="text-[10px] font-mono break-all">{operatorNpub}</p>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-3 leading-relaxed">
          Log in with the operator key to manage verifications. If you believe this
          IS the operator key, the mismatch is in <span className="font-mono">OPERATOR_PUBKEY</span> in{' '}
          <span className="font-mono">src/lib/platform.ts</span>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg bg-primary/5 border border-primary/20 px-3 py-2 mb-2">
        <p className="text-xs font-semibold text-primary flex items-center gap-1.5">
          <ShieldAlert className="w-3.5 h-3.5" /> Operator tools
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          Verification management for BCHNostr. Only you can see this tab.
        </p>
      </div>

      {/* Site Metrics — links to the admin-only /metrics dashboard. It has no
          nav link anywhere else, so this is the only way to reach it in-app. */}
      <Link
        to="/metrics"
        className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 hover:border-primary/40 hover:bg-primary/5 transition-colors group"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <BarChart3 className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold">Site Metrics</p>
          <p className="text-[10px] text-muted-foreground">Community dashboard — relay-sampled estimates</p>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary transition-colors" />
      </Link>

      <AutoGrantsPanel />
      <BusinessVerificationAdmin />
      <TurnServerPanel />
      <Nip05RecoveryPanel />
      <ManualVerificationSettings />
    </div>
  );
}

// ── BCH Rooms relay (TURN) — Operator Only ──────────────────────
//
// Peer-to-peer audio fails outright for anyone behind symmetric NAT or
// CGNAT — which is most mobile carriers — unless a TURN server can relay
// the audio. Without one those users just see "can't connect".
//
// This panel accepts **static long-term credentials**, so the whole thing
// is paste-and-save with no backend to deploy. ExpressTURN's free tier
// (1000 GB/month) works this way; so does self-hosted coturn with
// `lt-cred-mech`. Providers that mint short-lived credentials from a
// secret can't be used here, because that secret must never be shipped to
// a browser.
//
// The settings are per-browser (localStorage), so this configures the
// operator's own client. Every user's browser needs the same values to
// benefit — see the note in the UI about baking them into the build.

function TurnServerPanel() {
  const { publish } = useNostrPublish();
  const [expanded, setExpanded] = useState(false);
  const [host, setHost] = useState('');
  const [username, setUsername] = useState('');
  const [credential, setCredential] = useState('');
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; detail: string } | null>(null);
  const [publishState, setPublishState] = useState<'idle' | 'publishing' | 'done' | 'error'>('idle');
  const [publishError, setPublishError] = useState('');

  // Load any existing config so the panel reflects reality.
  useEffect(() => {
    const cfg = getTurnConfig();
    if (!cfg) return;
    const first = Array.isArray(cfg.urls) ? cfg.urls[0] : cfg.urls;
    // Pull the bare hostname back out of `turn:host:3478?transport=udp`.
    const match = /^turns?:([^:?]+)/i.exec(first || '');
    if (match) setHost(match[1]);
    setUsername(cfg.username || '');
    setCredential(cfg.credential || '');
  }, []);

  const configured = hasTurn();
  // A local override means it works for the operator but nobody else — the
  // status must say so, or "ON" would imply users are covered when they
  // are not.
  const localOnly = hasLocalTurnOverride() && !getCachedSharedTurn();

  const handleSave = () => {
    const cleanHost = host.trim().replace(/^turns?:/i, '').replace(/:\d+.*$/, '');
    if (!cleanHost || !username.trim() || !credential.trim()) return;
    setTurnConfig({
      urls: buildStaticTurnUrls(cleanHost),
      username: username.trim(),
      credential: credential.trim(),
    });
    setSaved(true);
    setTestResult(null);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleClear = () => {
    setTurnConfig(null);
    setHost('');
    setUsername('');
    setCredential('');
    setTestResult(null);
    setPublishState('idle');
  };

  /**
   * Publish the relay to every user via NIP-78 (kind 30078).
   *
   * Clients only honour this event from OPERATOR_PUBKEY, so it is safe to
   * distribute over public relays — but the credential itself becomes
   * readable by anyone, exactly as it would be if baked into the JS
   * bundle. The advantage over hard-coding is rotation: republish, and
   * every client picks up the new value with no redeploy.
   */
  const handlePublish = useCallback(async () => {
    const cleanHost = host.trim().replace(/^turns?:/i, '').replace(/:\d+.*$/, '');
    if (!cleanHost || !username.trim() || !credential.trim()) return;

    setPublishState('publishing');
    setPublishError('');
    try {
      await publish({
        kind: 30078,
        content: JSON.stringify({
          urls: buildStaticTurnUrls(cleanHost),
          username: username.trim(),
          credential: credential.trim(),
        }),
        tags: [
          ['d', TURN_CONFIG_D_TAG],
          ['alt', 'BCH Rooms TURN relay configuration'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setPublishState('done');
      setTimeout(() => setPublishState('idle'), 4000);
    } catch (err) {
      setPublishState('error');
      setPublishError(err instanceof Error ? err.message : 'Could not publish.');
    }
  }, [host, username, credential, publish]);

  /**
   * Withdraw the shared relay by publishing an empty config, so clients
   * stop using it. Needed if a credential leaks or the free tier runs out.
   */
  const handleUnpublish = useCallback(async () => {
    setPublishState('publishing');
    setPublishError('');
    try {
      await publish({
        kind: 30078,
        content: '{}',
        tags: [
          ['d', TURN_CONFIG_D_TAG],
          ['alt', 'BCH Rooms TURN relay configuration (disabled)'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      cacheSharedTurnConfig(null);
      setPublishState('idle');
    } catch (err) {
      setPublishState('error');
      setPublishError(err instanceof Error ? err.message : 'Could not publish.');
    }
  }, [publish]);

  /**
   * Verify the relay actually works by gathering ICE candidates with
   * `iceTransportPolicy: 'relay'`, which forces every candidate through
   * TURN. If a `relay` candidate appears, the credentials are good; if
   * none do, they're wrong or the server is unreachable. This is the only
   * honest way to test — a saved config that silently fails is worse than
   * none, since it looks configured while helping nobody.
   */
  const handleTest = useCallback(async () => {
    setTesting(true);
    setTestResult(null);

    const cleanHost = host.trim().replace(/^turns?:/i, '').replace(/:\d+.*$/, '');
    if (!cleanHost || !username.trim() || !credential.trim()) {
      setTesting(false);
      setTestResult({ ok: false, detail: 'Fill in all three fields first.' });
      return;
    }

    let pc: RTCPeerConnection | null = null;
    try {
      pc = new RTCPeerConnection({
        iceServers: [{
          urls: buildStaticTurnUrls(cleanHost),
          username: username.trim(),
          credential: credential.trim(),
        }],
        iceTransportPolicy: 'relay',
      });

      const relayFound = await new Promise<boolean>(resolve => {
        let done = false;
        const finish = (found: boolean) => {
          if (done) return;
          done = true;
          resolve(found);
        };

        pc!.onicecandidate = e => {
          if (!e.candidate) { finish(false); return; } // gathering complete
          if (e.candidate.candidate.includes(' typ relay')) finish(true);
        };
        // TURN over a bad host can hang until the ICE timeout, so cap it.
        setTimeout(() => finish(false), 10000);

        pc!.createDataChannel('probe');
        pc!.createOffer()
          .then(o => pc!.setLocalDescription(o))
          .catch(() => finish(false));
      });

      setTestResult(relayFound
        ? { ok: true, detail: 'Relay candidate received — TURN is working.' }
        : { ok: false, detail: 'No relay candidate. Check the host and credentials.' });
    } catch {
      setTestResult({ ok: false, detail: 'Could not start the test.' });
    } finally {
      try { pc?.close(); } catch { /* already closed */ }
      setTesting(false);
    }
  }, [host, username, credential]);

  return (
    <div className="rounded-lg border border-border bg-background overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-primary/5 transition-colors text-left"
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Mic2 className="h-4 w-4 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold">BCH Rooms Relay (TURN)</p>
          <p className="text-[10px] text-muted-foreground">
            {localOnly
              ? 'Set in this browser only — not published yet'
              : configured
                ? 'Live for all users — mobile can connect'
                : 'Not set — some mobile users can\'t connect'}
          </p>
        </div>
        <span
          className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0 ${
            configured
              ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
              : 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
          }`}
        >
          {configured ? 'ON' : 'OFF'}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-border p-3 space-y-3">
          <div className="rounded-lg bg-muted/40 p-2.5 space-y-1.5">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Audio in BCH Rooms goes directly between people. That fails on
              networks that block direct connections — most mobile carriers —
              unless a relay can pass the audio through. Those users currently
              see <span className="font-semibold">"can't connect"</span>.
            </p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Get free credentials from{' '}
              <a
                href="https://www.expressturn.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                expressturn.com
              </a>
              {' '}(1000 GB/month, no card needed). Sign up, then copy the
              server host, username and password from the dashboard.
            </p>
          </div>

          <div className="space-y-2">
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                Relay host
              </label>
              <input
                value={host}
                onChange={e => setHost(e.target.value)}
                placeholder="relay1.expressturn.com"
                spellCheck={false}
                autoCapitalize="none"
                className="mt-0.5 w-full bg-background rounded-lg px-2.5 py-2 text-xs font-mono border border-border focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                Username
              </label>
              <input
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder="from the provider dashboard"
                spellCheck={false}
                autoCapitalize="none"
                className="mt-0.5 w-full bg-background rounded-lg px-2.5 py-2 text-xs font-mono border border-border focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                Password
              </label>
              <input
                value={credential}
                onChange={e => setCredential(e.target.value)}
                placeholder="from the provider dashboard"
                spellCheck={false}
                autoCapitalize="none"
                className="mt-0.5 w-full bg-background rounded-lg px-2.5 py-2 text-xs font-mono border border-border focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              className="flex-1 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
            >
              {saved ? 'Saved!' : 'Save'}
            </button>
            <button
              onClick={() => { void handleTest(); }}
              disabled={testing}
              className="px-3 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-muted disabled:opacity-50 flex items-center gap-1.5"
            >
              {testing && <Loader2 className="w-3 h-3 animate-spin" />}
              {testing ? 'Testing…' : 'Test'}
            </button>
            {configured && (
              <button
                onClick={handleClear}
                className="px-3 py-2 rounded-lg border border-red-500/40 text-red-500 text-xs font-semibold hover:bg-red-500/10"
              >
                Clear
              </button>
            )}
          </div>

          {testResult && (
            <div
              className={`rounded-lg p-2.5 flex items-start gap-2 ${
                testResult.ok
                  ? 'bg-emerald-500/10 border border-emerald-500/30'
                  : 'bg-red-500/10 border border-red-500/30'
              }`}
            >
              {testResult.ok
                ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-px" />
                : <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-px" />}
              <p className={`text-[10px] leading-relaxed ${
                testResult.ok
                  ? 'text-emerald-700 dark:text-emerald-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {testResult.detail}
              </p>
            </div>
          )}

          {/* ── Turn it on for everyone ── */}
          <div className="border-t border-border pt-2.5 space-y-2">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              <span className="font-semibold">Save</span> applies the relay to
              this browser only — useful for testing. Once{' '}
              <span className="font-semibold">Test</span> passes, publish it so
              every visitor's rooms use it too.
            </p>

            <button
              onClick={() => { void handlePublish(); }}
              disabled={publishState === 'publishing' || !testResult?.ok}
              className="w-full py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40 flex items-center justify-center gap-1.5"
            >
              {publishState === 'publishing' && <Loader2 className="w-3 h-3 animate-spin" />}
              {publishState === 'done'
                ? 'Published to all users!'
                : publishState === 'publishing'
                  ? 'Publishing…'
                  : 'Publish to all users'}
            </button>

            {/* Gate publishing on a passing test: shipping a broken relay to
                everyone is worse than shipping none, because it looks
                configured while still failing every connection. */}
            {!testResult?.ok && (
              <p className="text-[10px] text-muted-foreground">
                Run <span className="font-semibold">Test</span> first — it has
                to pass before this can be published.
              </p>
            )}

            {publishState === 'error' && (
              <p className="text-[10px] text-red-500">{publishError}</p>
            )}

            <button
              onClick={() => { void handleUnpublish(); }}
              disabled={publishState === 'publishing'}
              className="w-full py-1.5 rounded-lg border border-border text-[10px] font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
              Withdraw shared relay (stop all users using it)
            </button>

            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Published as a signed Nostr event only your operator key can
              sign, so it can be rotated any time without redeploying. The
              credential is readable by anyone — the same as if it were built
              into the app — so rotate it in the provider dashboard if the
              free tier gets abused.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Recent automated verifications (Operator Only) ──────────────
//
// Shows verification GRANT events issued by the automated backend
// (netlify/functions/verify-claims) or the operator. Read-only: it's a
// window into what the pay-to-verify pipeline has done, so the operator
// can confirm it's working without digging through Netlify logs.

const GRANT_OVERRIDE_PREFIX = 'bchnostr/nip05-override/';

interface AutoGrantRow {
  name: string;
  pubkey: string;
  grantedAt: number;
  txid?: string;
  usd?: number;
  auto: boolean;
  issuer: string;
}

function timeAgo(secs: number): string {
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - secs);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function AutoGrantRowItem({ row }: { row: AutoGrantRow }) {
  const { profile } = useNostrProfile(row.pubkey);
  const display = profile?.display_name || profile?.name || `${row.pubkey.slice(0, 8)}…`;
  const npub = (() => { try { return nip19.npubEncode(row.pubkey); } catch { return row.pubkey; } })();

  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/10">
        <BadgeCheck className="h-4 w-4 text-emerald-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-xs font-bold">{row.name}@bchnostr.com</span>
          {row.auto ? (
            <span className="shrink-0 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold text-emerald-600 dark:text-emerald-400">AUTO</span>
          ) : (
            <span className="shrink-0 rounded-full bg-primary/15 px-1.5 py-0.5 text-[9px] font-bold text-primary">MANUAL</span>
          )}
        </div>
        <p className="truncate text-[10px] text-muted-foreground" title={npub}>{display}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className="text-[11px] font-semibold tabular-nums">
          {typeof row.usd === 'number' ? `$${row.usd.toFixed(2)}` : '—'}
        </p>
        <p className="text-[9px] text-muted-foreground">{timeAgo(row.grantedAt)}</p>
      </div>
      {row.txid && (
        <a
          href={`https://bchexplorer.cash/tx/${row.txid}`}
          target="_blank"
          rel="noopener noreferrer"
          title="View payment on-chain"
          className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );
}

function AutoGrantsPanel() {
  const { user } = useAppContext();
  const isOperatorUser = isOperatorPubkey(user?.pubkey);

  // All verification grants ever issued by a trusted issuer.
  const { events, loading, refetch } = useNostrQuery({
    filters: [{ kinds: [30078], authors: GRANT_ISSUERS, limit: 2000 }],
    allRelays: true,
    enabled: isOperatorUser,
  });

  const rows = useMemo<AutoGrantRow[]>(() => {
    const byName = new Map<string, AutoGrantRow>();
    for (const ev of events) {
      const d = ev.tags.find((t: string[]) => t[0] === 'd')?.[1] || '';
      if (!d.startsWith(GRANT_OVERRIDE_PREFIX)) continue;
      const name = d.slice(GRANT_OVERRIDE_PREFIX.length).trim().toLowerCase();
      if (!name) continue;

      let pubkey = ev.tags.find((t: string[]) => t[0] === 'p')?.[1];
      let usd: number | undefined;
      let auto = false;
      let txid = ev.tags.find((t: string[]) => t[0] === 'fee')?.[1];
      try {
        const body = JSON.parse(ev.content) as { pubkey?: string; usd?: number; auto?: boolean; txid?: string };
        if (!pubkey && typeof body.pubkey === 'string') pubkey = body.pubkey;
        if (typeof body.usd === 'number') usd = body.usd;
        if (body.auto === true) auto = true;
        if (!txid && typeof body.txid === 'string') txid = body.txid;
      } catch { /* no body */ }
      if (!pubkey) continue;

      const row: AutoGrantRow = {
        name,
        pubkey: pubkey.trim().toLowerCase(),
        grantedAt: ev.created_at,
        txid: txid?.trim().toLowerCase(),
        usd,
        auto,
        issuer: ev.pubkey,
      };
      // Keep the newest grant per name.
      const prev = byName.get(name);
      if (!prev || row.grantedAt > prev.grantedAt) byName.set(name, row);
    }
    return [...byName.values()].sort((a, b) => b.grantedAt - a.grantedAt);
  }, [events]);

  if (!isOperatorUser) return null;

  const autoCount = rows.filter(r => r.auto).length;

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <BadgeCheck className="h-4 w-4 text-emerald-500" />
        <h3 className="text-sm font-bold text-emerald-600 dark:text-emerald-400">Recent Verifications</h3>
        <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-600 dark:text-emerald-400">AUTO-GRANT</span>
        <button
          onClick={() => refetch?.()}
          title="Refresh"
          className="ml-auto rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Verification grants issued by the automated pay-to-verify backend. When a
        user pays the fee, a grant appears here (and their badge goes live) within
        about two minutes — no action needed from you.
      </p>

      <div className="mb-3 grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-muted/50 p-2 text-center">
          <p className="text-lg font-bold">{rows.length}</p>
          <p className="text-[9px] uppercase tracking-wide text-muted-foreground">Total granted</p>
        </div>
        <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-2 text-center">
          <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{autoCount}</p>
          <p className="text-[9px] uppercase tracking-wide text-muted-foreground">Automated</p>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center gap-2 py-6">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <p className="text-xs text-muted-foreground">Scanning relays for grants…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg bg-muted/40 p-3 text-center">
          <p className="text-xs text-muted-foreground">
            No verification grants found yet. Once someone pays and the backend
            confirms it on-chain, their grant shows up here.
          </p>
        </div>
      ) : (
        <div className="max-h-80 space-y-1.5 overflow-y-auto scrollbar-hide">
          {rows.slice(0, 100).map(r => (
            <AutoGrantRowItem key={r.name} row={r} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Paid-claim recovery (Operator Only) ─────────────────────────
//
// Every paid verification published a kind 30078 receipt with the
// d-tag `bchnostr/nip05/<name>`. Those receipts are still on the relays,
// so they are the authoritative record of who paid — even if the
// directory file was lost.
//
// This panel replays them, cross-references the live directory, and
// generates the JSON to drop into public/.well-known/nostr.json.

interface ClaimRow {
  name: string;
  pubkey: string;
  claimedAt: number;
  /** Payment txid, when the claim recorded one. */
  txid?: string;
  /** Satoshis the claim says were paid. */
  amountSats?: number;
  /** Already correctly listed in the live directory. */
  live: boolean;
  /** Directory maps this name to a *different* pubkey. */
  conflict: boolean;
  conflictWith?: string;
}

/** Pull the payment txid out of a claim event, tag first then content. */
function extractClaimPayment(ev: { tags: string[][]; content: string }): {
  txid?: string;
  amountSats?: number;
} {
  // Preferred: the `fee` tag — ['fee', txid, sats, address]
  const feeTag = ev.tags.find((t: string[]) => t[0] === 'fee');
  if (feeTag?.[1]) {
    const sats = feeTag[2] ? parseInt(feeTag[2], 10) : undefined;
    return {
      txid: feeTag[1].trim().toLowerCase(),
      amountSats: Number.isFinite(sats) ? sats : undefined,
    };
  }
  // Fallback: the JSON body, for claims that recorded it there only.
  try {
    const body = JSON.parse(ev.content) as { txid?: unknown; amountSats?: unknown };
    const txid = typeof body.txid === 'string' ? body.txid.trim().toLowerCase() : undefined;
    const amountSats = typeof body.amountSats === 'number' ? body.amountSats : undefined;
    return { txid, amountSats };
  } catch {
    return {};
  }
}

function Nip05RecoveryPanel() {
  const { user } = useAppContext();
  const isOperatorUser = isOperatorPubkey(user?.pubkey);

  const [copied, setCopied] = useState(false);
  const [dirLoaded, setDirLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(true);

  // Pull every paid claim receipt ever published. Hooks must run
  // unconditionally, so gate the *query* rather than returning early.
  const { events, loading } = useNostrQuery({
    filters: [{ kinds: [NIP05_CLAIM_KIND], '#t': ['bch-nip05'], limit: 1000 }],
    allRelays: true,
    enabled: isOperatorUser,
  });

  // Make sure we're comparing against a fresh directory.
  useEffect(() => {
    if (!isOperatorUser) return;
    let cancelled = false;
    refreshDirectory(true)
      .then(() => { if (!cancelled) { setDirLoaded(true); setRefreshing(false); } })
      .catch(() => { if (!cancelled) setRefreshing(false); });
    return () => { cancelled = true; };
  }, [isOperatorUser]);

  const rows = useMemo<ClaimRow[]>(() => {
    const directory = getDirectoryNames();
    // Keep the earliest claim per name — whoever paid for it first owns it.
    const byName = new Map<string, {
      pubkey: string; claimedAt: number; txid?: string; amountSats?: number;
    }>();

    for (const ev of events) {
      const dTag = ev.tags.find((t: string[]) => t[0] === 'd')?.[1] || '';
      if (!dTag.startsWith(NIP05_D_TAG_PREFIX)) continue;
      const name = dTag.slice(NIP05_D_TAG_PREFIX.length).trim().toLowerCase();
      if (!name || !/^[a-z0-9_.-]+$/.test(name)) continue;

      const existing = byName.get(name);
      if (!existing || ev.created_at < existing.claimedAt) {
        byName.set(name, {
          pubkey: ev.pubkey.toLowerCase(),
          claimedAt: ev.created_at,
          ...extractClaimPayment(ev),
        });
      }
    }

    return [...byName.entries()]
      .map(([name, { pubkey, claimedAt, txid, amountSats }]) => {
        const mapped = directory[name];
        return {
          name,
          pubkey,
          claimedAt,
          txid,
          amountSats,
          live: mapped === pubkey,
          conflict: !!mapped && mapped !== pubkey,
          conflictWith: mapped && mapped !== pubkey ? mapped : undefined,
        };
      })
      .sort((a, b) => a.claimedAt - b.claimedAt);
  }, [events, dirLoaded]);

  // ── On-chain audit (operator-initiated) ──
  // Not automatic: it costs one explorer request per claim, and the
  // result never changes who gets a badge — it's advisory.
  const [audit, setAudit] = useState<Map<string, AuditResult> | null>(null);
  const [auditing, setAuditing] = useState(false);
  const [auditProgress, setAuditProgress] = useState({ done: 0, total: 0 });

  const runAudit = async () => {
    setAuditing(true);
    setAudit(null);
    try {
      const results = await auditClaims(
        rows.map(r => ({
          name: r.name,
          pubkey: r.pubkey,
          claimedAt: r.claimedAt,
          txid: r.txid,
          amountSats: r.amountSats,
        })),
        PLATFORM_FEE_ADDRESS,
        undefined,
        (done, total) => setAuditProgress({ done, total }),
      );
      setAudit(results);
    } catch {
      // Leave audit null — the panel simply shows no verdicts.
    }
    setAuditing(false);
  };

  const suspiciousCount = useMemo(() => {
    if (!audit) return 0;
    let n = 0;
    for (const r of audit.values()) if (isSuspicious(r.verdict)) n++;
    return n;
  }, [audit]);

  // Only non-conflicting, not-yet-live claims need adding.
  const missing = useMemo(() => rows.filter(r => !r.live && !r.conflict), [rows]);
  const conflicts = useMemo(() => rows.filter(r => r.conflict), [rows]);
  const liveCount = rows.filter(r => r.live).length;

  const generatedJson = useMemo(() => {
    const additions: Record<string, string> = {};
    for (const r of missing) additions[r.name] = r.pubkey;
    return buildDirectoryJson(additions);
  }, [missing, dirLoaded]);

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedJson).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // All hooks have run — safe to bail out for non-operators.
  if (!isOperatorUser) return null;

  const busy = loading || refreshing;

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <BadgeCheck className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-bold text-primary">Recover Paid Verifications</h3>
        <span className="text-[9px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-semibold">OPERATOR</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Finds everyone who ever paid for verification by replaying their kind 30078
        payment receipts from the relays, then generates the directory file. Users
        already listed correctly are left untouched.
      </p>

      {busy ? (
        <div className="flex items-center gap-2 py-6 justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-primary" />
          <p className="text-xs text-muted-foreground">
            {refreshing ? 'Checking live directory…' : 'Scanning relays for payment receipts…'}
          </p>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="rounded-lg bg-muted/50 p-2 text-center">
              <p className="text-lg font-bold">{rows.length}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Paid</p>
            </div>
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-2 text-center">
              <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{liveCount}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Verified</p>
            </div>
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-2 text-center">
              <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{missing.length}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wide">Missing</p>
            </div>
          </div>

          {rows.length === 0 && (
            <div className="rounded-lg bg-muted/40 p-3 text-center">
              <p className="text-xs text-muted-foreground">
                No payment receipts found on the connected relays. If you know users paid,
                their claim events may only exist on relays this client isn't reading —
                add those relays in the Relays tab and try again.
              </p>
            </div>
          )}

          {/* Conflicts need a human decision */}
          {conflicts.length > 0 && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-3 mb-3">
              <p className="text-xs font-bold text-red-500 mb-1.5 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                {conflicts.length} conflict{conflicts.length !== 1 ? 's' : ''} — excluded, resolve manually
              </p>
              <p className="text-[10px] text-muted-foreground mb-2">
                The directory already maps these names to a different key than the
                earliest paid claim. Left out so this tool can't reassign someone's name.
              </p>
              <div className="space-y-1">
                {conflicts.map(r => (
                  <div key={r.name} className="text-[10px] font-mono">
                    <span className="font-bold">{r.name}</span>
                    <span className="text-muted-foreground"> claim:{r.pubkey.slice(0, 12)}… live:{r.conflictWith?.slice(0, 12)}…</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Missing users */}
          {missing.length > 0 && (
            <div className="space-y-1.5 mb-3 max-h-56 overflow-y-auto">
              {missing.map(r => (
                <div key={r.name} className="flex items-center gap-2 rounded-lg bg-muted/40 px-2.5 py-1.5">
                  <BadgeCheck className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold truncate">{r.name}@{NIP05_DOMAIN}</p>
                    <p className="text-[9px] text-muted-foreground font-mono truncate">{r.pubkey}</p>
                  </div>
                  <span className="text-[9px] text-muted-foreground shrink-0">
                    {new Date(r.claimedAt * 1000).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* On-chain audit */}
          {rows.length > 0 && (
            <div className="rounded-lg border border-border bg-muted/30 p-3 mb-3">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <p className="text-xs font-bold">On-chain payment audit</p>
                <button
                  onClick={runAudit}
                  disabled={auditing}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-muted text-[11px] font-semibold hover:bg-muted/70 transition-colors disabled:opacity-50"
                >
                  {auditing
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> {auditProgress.done}/{auditProgress.total}</>
                    : <><Search className="w-3 h-3" /> {audit ? 'Re-run' : 'Run audit'}</>}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                Checks each claim's recorded txid against the chain — that it exists,
                paid <span className="font-mono">{PLATFORM_FEE_ADDRESS.replace(/^bitcoincash:/, '').slice(0, 10)}…</span>,
                and paid enough. Advisory only: it never changes who holds a badge.
              </p>

              {audit && (
                <>
                  <div className="flex items-center gap-3 mt-2 pt-2 border-t border-border/60">
                    <p className="text-[10px]">
                      <span className="font-bold text-emerald-600 dark:text-emerald-400">
                        {[...audit.values()].filter(r => r.verdict === 'paid').length}
                      </span>
                      <span className="text-muted-foreground"> confirmed</span>
                    </p>
                    <p className="text-[10px]">
                      <span className="font-bold text-muted-foreground">
                        {[...audit.values()].filter(r => r.verdict === 'no-record').length}
                      </span>
                      <span className="text-muted-foreground"> no record</span>
                    </p>
                    <p className="text-[10px]">
                      <span className={`font-bold ${suspiciousCount > 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                        {suspiciousCount}
                      </span>
                      <span className="text-muted-foreground"> flagged</span>
                    </p>
                  </div>

                  {/* Only surface rows that need a human decision. */}
                  {suspiciousCount > 0 && (
                    <div className="mt-2 space-y-1">
                      {rows.filter(r => {
                        const v = audit.get(r.name);
                        return v && isSuspicious(v.verdict);
                      }).map(r => {
                        const v = audit.get(r.name)!;
                        return (
                          <div key={r.name} className="rounded bg-red-500/10 border border-red-500/20 px-2 py-1.5">
                            <p className="text-[10px] font-bold text-red-500">
                              {r.name}@{NIP05_DOMAIN}
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                              {describeVerdict(v.verdict)}
                              {v.paidSats !== undefined && v.expectedSats !== undefined && (
                                <> — paid {v.paidSats.toLocaleString()} of {v.expectedSats.toLocaleString()} sats</>
                              )}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {[...audit.values()].some(r => r.verdict === 'no-record') && (
                    <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
                      <Info className="w-3 h-3 inline-block mr-0.5 align-text-bottom" />
                      "No record" means the claim predates txid tracking — it is not
                      evidence of non-payment. Claims made from now on record their txid.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Generated file */}
          {rows.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  public/.well-known/nostr-baseline.json
                </label>
                <button
                  onClick={handleCopy}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors ${
                    copied ? 'bg-emerald-500 text-white' : 'bg-primary text-primary-foreground hover:bg-primary/90'
                  }`}
                >
                  {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <pre className="rounded-lg bg-muted/60 border border-border p-2.5 text-[10px] font-mono overflow-auto max-h-52 leading-relaxed">
                {generatedJson}
              </pre>
              {missing.length > 0 && (
                <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">
                  Two ways to apply this. For a one-off bulk restore, paste it into{' '}
                  <span className="font-mono">public/.well-known/nostr-baseline.json</span> and
                  redeploy. For individual users — or to avoid a deploy entirely — use
                  Manual Verification below, which takes effect live.
                </p>
              )}
              {missing.length === 0 && conflicts.length === 0 && (
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-2 font-medium flex items-center gap-1.5">
                  <Check className="w-3 h-3" /> Everyone who paid is verified. No action needed.
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function ManualVerificationSettings() {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const [name, setName] = useState('');
  const [pubkey, setPubkey] = useState('');
  const [status, setStatus] = useState<'idle' | 'publishing' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Only visible to operator
  if (!isOperatorPubkey(user?.pubkey)) return null;

  const normalizedName = name.trim().toLowerCase().replace(/[^a-z0-9_.-]/g, '');
  const isValidName = normalizedName.length >= 1 && normalizedName.length <= 30;

  // Support both hex and npub input
  const resolvedPubkey = (() => {
    const trimmed = pubkey.trim();
    if (/^[0-9a-f]{64}$/i.test(trimmed)) return trimmed.toLowerCase();
    if (trimmed.startsWith('npub1')) {
      try {
        const decoded = nip19.decode(trimmed);
        if (decoded.type === 'npub') return decoded.data as string;
      } catch {}
    }
    return '';
  })();

  const canSubmit = isValidName && resolvedPubkey.length === 64 && status === 'idle';

  const handleOverride = async () => {
    if (!canSubmit) return;
    setStatus('publishing');
    setErrorMsg('');
    try {
      const now = Math.floor(Date.now() / 1000);
      await publish({
        kind: NIP05_OVERRIDE_KIND,
        content: JSON.stringify({
          name: normalizedName,
          pubkey: resolvedPubkey,
          domain: NIP05_OVERRIDE_DOMAIN,
          grantedAt: now,
          grantedBy: user.pubkey,
        }),
        tags: [
          ['d', NIP05_OVERRIDE_D_TAG_PREFIX + normalizedName],
          ['p', resolvedPubkey],
          ['t', 'bch-nip05-override'],
        ],
        created_at: now,
      });

      // Invalidate cache so it picks up immediately
      invalidateNip05Cache(`${normalizedName}@${NIP05_OVERRIDE_DOMAIN}`, resolvedPubkey);

      setStatus('done');
      setTimeout(() => { setStatus('idle'); setName(''); setPubkey(''); }, 3000);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : 'Failed to publish override');
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <BadgeCheck className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-bold text-primary">Manual Verification (Admin)</h3>
        <span className="text-[9px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full font-semibold">OPERATOR</span>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Grant a @bchnostr.com NIP-05 to a user manually — use this after confirming
        they paid. The grant is published to Nostr and the directory function picks
        it up live, so <strong>no redeploy is needed</strong>. Allow up to 5 minutes
        for the edge cache to expire.
      </p>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">Username</label>
          <div className="flex items-center gap-0">
            <input
              value={name}
              onChange={e => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_.-]/g, ''))}
              placeholder="username"
              maxLength={30}
              className="flex-1 bg-muted rounded-l-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <span className="bg-muted/60 border-l border-border px-3 py-2 text-sm text-muted-foreground rounded-r-lg font-mono">
              @{NIP05_OVERRIDE_DOMAIN}
            </span>
          </div>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">User's Public Key</label>
          <input
            value={pubkey}
            onChange={e => setPubkey(e.target.value)}
            placeholder="npub1... or hex pubkey"
            className="w-full bg-muted rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {pubkey.trim() && !resolvedPubkey && (
            <p className="text-[10px] text-red-500 mt-1">Invalid pubkey — enter a 64-char hex key or npub</p>
          )}
          {resolvedPubkey && (
            <p className="text-[10px] text-emerald-500 mt-1 font-mono truncate">{resolvedPubkey}</p>
          )}
        </div>

        <button
          onClick={handleOverride}
          disabled={!canSubmit}
          className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
            status === 'done' ? 'bg-emerald-500 text-white'
            : status === 'error' ? 'bg-red-500 text-white'
            : 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
          }`}
        >
          {status === 'publishing' ? <><Loader2 className="w-4 h-4 animate-spin" /> Publishing...</>
            : status === 'done' ? <><Check className="w-4 h-4" /> Verification Granted!</>
            : status === 'error' ? errorMsg.slice(0, 40)
            : <><BadgeCheck className="w-4 h-4" /> Grant Verification</>
          }
        </button>
      </div>
    </div>
  );
}

function MediaSettings() {
  return (
    <div className="space-y-6">
      {/* Upload limits info */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Media Upload Services</h3>
        <p className="text-xs text-muted-foreground mb-4">
          BCH Nostr uploads your photos, videos, and audio to decentralized Nostr media servers.
          Files are stored permanently and served via CDN.
        </p>

        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div>
              <p className="text-sm font-medium">Images</p>
              <p className="text-xs text-muted-foreground">Up to 10 MB per image</p>
            </div>
            <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded">Free</span>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div>
              <p className="text-sm font-medium">Audio</p>
              <p className="text-xs text-muted-foreground">Up to 50 MB per file</p>
            </div>
            <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded">Free</span>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <div>
              <p className="text-sm font-medium">Video</p>
              <p className="text-xs text-muted-foreground">Up to 500 MB per video (free servers)</p>
            </div>
            <span className="text-xs font-mono text-muted-foreground bg-muted px-2 py-1 rounded">Free</span>
          </div>
        </div>
      </div>

      {/* nostr.build Pro */}
      <div className="border border-primary/30 rounded-xl p-4 bg-primary/5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Film className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-sm font-bold flex items-center gap-1.5">
              nostr.build Pro
              <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full">Recommended</span>
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Premium media hosting with larger uploads, server-side transcoding, and a global CDN.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="p-2.5 rounded-lg bg-background border border-border">
            <p className="font-semibold">Purist</p>
            <p className="text-muted-foreground mt-0.5">$18/year &middot; 5 GB storage</p>
            <p className="text-primary font-medium mt-1">450 MB per upload</p>
          </div>
          <div className="p-2.5 rounded-lg bg-background border border-border">
            <p className="font-semibold">Professional</p>
            <p className="text-muted-foreground mt-0.5">$69/year &middot; 25 GB storage</p>
            <p className="text-primary font-medium mt-1">450 MB+ per upload</p>
          </div>
          <div className="p-2.5 rounded-lg bg-background border border-border">
            <p className="font-semibold">Creator</p>
            <p className="text-muted-foreground mt-0.5">$120/year &middot; 100 GB storage</p>
            <p className="text-primary font-medium mt-1">AI Studio + S3 backup</p>
          </div>
          <div className="p-2.5 rounded-lg bg-background border border-border">
            <p className="font-semibold">Advanced</p>
            <p className="text-muted-foreground mt-0.5">$240/year &middot; 500 GB storage</p>
            <p className="text-primary font-medium mt-1">NIP-05 + expandable</p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
            <span>Pay with Bitcoin Lightning or on-chain. No KYC.</span>
          </div>
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
            <span>Sign in with your Nostr key — the same one you use here.</span>
          </div>
          <div className="flex items-start gap-2 text-xs text-muted-foreground">
            <Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
            <span>No code changes needed. BCH Nostr auto-detects your plan when uploading.</span>
          </div>
        </div>

        <a
          href="https://nostr.build/plans/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
        >
          View Plans on nostr.build <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* Free services list */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Free Upload Servers</h3>
        <p className="text-xs text-muted-foreground mb-3">
          BCH Nostr automatically tries multiple servers. If one fails, it falls back to the next.
        </p>
        <div className="space-y-1.5">
          {[
            { name: 'nostr.build', limit: '20 MB free / 450 MB paid', type: 'Blossom' },
            { name: 'blossom.primal.net', limit: '100 MB', type: 'Blossom' },
            { name: 'nosto.re', limit: '500 MB', type: 'NIP-96' },
            { name: 'nostr.download', limit: '10 GB', type: 'NIP-96' },
            { name: 'nostrmedia.com', limit: '25 MB free / 100 MB paid', type: 'Blossom' },
            { name: 'nostpic.com', limit: '16 MB', type: 'NIP-96' },
          ].map(s => (
            <div key={s.name} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 text-xs">
              <span className="font-medium">{s.name}</span>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{s.limit}</span>
                <span className="text-[10px] font-mono text-muted-foreground/60 bg-muted px-1.5 py-0.5 rounded">{s.type}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
function AppearanceSettings({ theme, toggleTheme }: { theme: string; toggleTheme: () => void }) {
  const SCALE_KEY = 'bchnostr:textScale';
  const [textScale, setTextScale] = useState(() => {
    try { return parseFloat(localStorage.getItem(SCALE_KEY) || '1') || 1; } catch { return 1; }
  });
  const [dataSaver, setDataSaverState] = useState(isDataSaverEnabled);

  const applyScale = (scale: number) => {
    const clamped = Math.round(Math.max(0.75, Math.min(1.5, scale)) * 100) / 100;
    setTextScale(clamped);
    document.documentElement.style.setProperty('--text-scale', String(clamped));
    try { localStorage.setItem(SCALE_KEY, String(clamped)); } catch {}
  };

  const pct = Math.round(textScale * 100);

  return (
    <div className="space-y-4">
      {/* Theme toggle */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
        <div className="flex items-center gap-3">
          {theme === 'dark' ? <Moon className="w-5 h-5 text-primary" /> : <Sun className="w-5 h-5 text-amber-500" />}
          <div>
            <p className="text-sm font-medium">{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</p>
            <p className="text-xs text-muted-foreground">Toggle between light and dark theme</p>
          </div>
        </div>
        <button
          onClick={toggleTheme}
          className={`relative w-12 h-6 rounded-full transition-colors ${theme === 'dark' ? 'bg-primary' : 'bg-muted-foreground/30'}`}
        >
          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${theme === 'dark' ? 'translate-x-6' : 'translate-x-0.5'}`} />
        </button>
      </div>

      {/* Data Saver toggle */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
        <div className="flex items-center gap-3">
          <Download className={`w-5 h-5 ${dataSaver ? 'text-emerald-500' : 'text-muted-foreground'}`} />
          <div>
            <p className="text-sm font-medium">Data Saver</p>
            <p className="text-xs text-muted-foreground">
              {dataSaver ? 'Active — using less data' : 'Reduce mobile data usage'}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            const next = !dataSaver;
            setDataSaver(next);
            setDataSaverState(next);
          }}
          className={`relative w-12 h-6 rounded-full transition-colors ${dataSaver ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`}
        >
          <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${dataSaver ? 'translate-x-6' : 'translate-x-0.5'}`} />
        </button>
      </div>
      {dataSaver && (
        <div className="mx-3 -mt-2 text-[11px] text-emerald-600 dark:text-emerald-400 space-y-0.5">
          <p>• Images load only when scrolled into view</p>
          <p>• Fewer relay connections (saves bandwidth)</p>
          <p>• Smaller feed queries (less data on startup)</p>
          <p className="text-muted-foreground pt-1">Reload the page for full effect.</p>
        </div>
      )}

      {/* Text size slider */}
      <div className="p-3 rounded-lg bg-muted/50 space-y-3">
        <div className="flex items-center gap-3">
          <ALargeSmall className="w-5 h-5 text-primary shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium">Text Size</p>
            <p className="text-xs text-muted-foreground">Adjust how big or small text appears</p>
          </div>
          <span className="text-xs font-mono font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">{pct}%</span>
        </div>

        {/* Slider */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => applyScale(textScale - 0.05)}
            disabled={textScale <= 0.75}
            className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Smaller"
          >
            <Type className="w-3 h-3 text-muted-foreground" />
          </button>
          <input
            type="range"
            min="0.75"
            max="1.5"
            step="0.05"
            value={textScale}
            onChange={e => applyScale(parseFloat(e.target.value))}
            className="flex-1 h-1.5 rounded-full appearance-none bg-muted cursor-pointer accent-primary [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:cursor-pointer"
          />
          <button
            onClick={() => applyScale(textScale + 0.05)}
            disabled={textScale >= 1.5}
            className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Bigger"
          >
            <Type className="w-4.5 h-4.5 text-foreground" />
          </button>
        </div>

        {/* Quick presets */}
        <div className="flex items-center gap-1.5">
          {[
            { label: 'Small', value: 0.85 },
            { label: 'Default', value: 1.0 },
            { label: 'Large', value: 1.15 },
            { label: 'XL', value: 1.3 },
          ].map(preset => (
            <button
              key={preset.label}
              onClick={() => applyScale(preset.value)}
              className={`flex-1 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                Math.abs(textScale - preset.value) < 0.03
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border hover:bg-muted text-muted-foreground'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Preview */}
        <div className="rounded-lg border border-border bg-background p-3 space-y-1">
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Preview</p>
          <p className="text-sm">This is how posts and messages will look at {pct}% size.</p>
          <p className="text-xs text-muted-foreground">Smaller details like timestamps and labels also scale.</p>
        </div>

        {/* Reset */}
        {textScale !== 1 && (
          <button
            onClick={() => applyScale(1)}
            className="w-full py-2 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors flex items-center justify-center gap-1.5"
          >
            <RotateCcw className="w-3 h-3" />
            Reset to Default
          </button>
        )}
      </div>
    </div>
  );
}

